/**
 * chronos-admin.js v7.5
 *
 * v7.5 changes:
 *   - FIXED two syntax errors that prevented the whole file from parsing:
 *     clearAllBulkPricingForProduct had been pasted inside the body of
 *     clearBulkPricingForSelected, and saveVariantMetafield contained a
 *     duplicate copy of itself. Both are now single, top-level functions.
 *   - syncProducts: catalog entries now carry product_type (pt), tags (tg) and
 *     the Shopify product id (gid). pt/tg drive the Templates panel's type and
 *     tag filters; gid saves a lookup per product during bulk apply.
 *     RE-RUN SYNC after upgrading — products cached before this change have
 *     none of these fields and won't match those filters.
 *   - renderPickerList: says so when the 100-item render cap truncates the
 *     list, instead of silently showing 100 of 800.
 *   - Danger zone: clearAddonsForProduct + clearEverythingForProduct, wired to
 *     the buttons in the right-hand save bar. Add-ons live in ONE product
 *     metafield; bulk pricing lives in THREE metafields per VARIANT. The two
 *     are independent, which is why "Clear everything" is its own action.
 *   - Price summary labels: optional per-product overrides for the three
 *     storefront summary rows, stored as cfg.summary_labels {base, addons,
 *     total} and emitted only when at least one is set.
 */

const CATS = [
  {id:'driver',     label:'Driver / SMPS',  icon:'⚡', sub:'Power supply',     display:'image'},
  {id:'adapter',    label:'Adapter',        icon:'🔌', sub:'Plug & play power', display:'image'},
  {id:'controller', label:'Controller',     icon:'🎛️', sub:'RF / WiFi / BT',   display:'image'},
  {id:'dimmer',     label:'Dimmer',         icon:'💡', sub:'Brightness ctrl',   display:'button'},
  {id:'connector',  label:'Connector',      icon:'🔗', sub:'Strip connectors',  display:'button'},
  {id:'custom',     label:'Custom',         icon:'✏️', sub:'Anything else',     display:'button'},
];

let selectedProd   = null;
let fetchedVars    = [];
let fetchedImgs    = [];
let addonCfg       = {common:[], variant_rules:[], summary_labels:{}};
let selGrp         = null;
let dirty          = false;
let filteredProds  = [...ALL_PRODUCTS];

let agmSection = 'common';
let agmCatId   = 'custom';
let scopeMode  = 'all';
let scopeSel   = new Set();

let pickerTargetSec = null;
let pickerTargetGi  = null;
let pickerTargetOi  = null;
let pickerSelProd   = null;
let pickerSelVars   = [];
let pickerFiltered  = [];
let pickerCache     = {};
let configuredProducts = [];
let configuredFiltered = [];

// ── BULK PRICING ──
let centerMode             = 'addons'; // 'addons' | 'bulk'
let bulkSelectedVariantId  = null;
let bulkTiers              = []; // [{q:Number, p:Number}, ...] — tier 1 (base price) is never stored here
let bulkBestQty            = null; // quantity threshold of the tier marked "best seller / top choice" (1 = the base tier), or null for none
let bulkLoadingVariant     = false;
let bulkApplyMode          = false;    // false = single-variant editor, true = apply-to-multiple
let bulkApplyPricingMode   = 'pct';    // 'pct' = % off each variant's own price | 'fixed' = same Rs price for every selected variant
let bulkApplyTiers         = [{q: 10, val: 5}]; // [{q:Number, val:Number}, ...] — val means % or Rs depending on bulkApplyPricingMode
let bulkApplyBestQty       = null; // quantity threshold marked best, applied to every variant in the group
let bulkApplySelected      = new Set(); // variant ids chosen for "Apply to multiple"

// ── INIT ──
(function init() {
  // Load previously synced full catalog (overwrites static list entries with live data)
  const syncCache = localStorage.getItem('ca_synced_products');
  if (syncCache) {
    try {
      const synced = JSON.parse(syncCache);
      synced.forEach(sp => {
        const idx = ALL_PRODUCTS.findIndex(p => p.h === sp.h);
        if (idx >= 0) ALL_PRODUCTS[idx] = sp;
        else ALL_PRODUCTS.push(sp);
      });
    } catch(e) {}
  }
  // Also load individually-fetched live products
  const liveCache = localStorage.getItem('ca_live_products');
  if (liveCache) {
    try {
      const live = JSON.parse(liveCache);
      live.forEach(lp => {
        if (!ALL_PRODUCTS.find(p => p.h === lp.h)) ALL_PRODUCTS.push(lp);
      });
    } catch(e) {}
  }
  filteredProds = [...ALL_PRODUCTS];
  renderProdList(ALL_PRODUCTS);
  document.getElementById('pcnt').textContent = ALL_PRODUCTS.length + ' products';
  document.addEventListener('DOMContentLoaded', updateSyncLabel);
  loadConfiguredCache();
  updateConfiguredLabel();
})();


// ── SYNC ALL PRODUCTS FROM SHOPIFY ──
async function syncProducts() {
  const token    = localStorage.getItem('ca_shpat') || '';
  const proxyUrl = (localStorage.getItem('ca_proxy_url') || '').replace(/\/$/, '');
  if (!token || !proxyUrl) {
    showToast('Add API token + Proxy URL in Settings first', 'error');
    openProxySettings();
    return;
  }

  const btn = document.getElementById('btn-sync');
  if (btn) { btn.disabled = true; btn.textContent = '⟳ Syncing…'; }
  showToast('Syncing products from Shopify…', 'info');

  try {
    let allProducts = [];
    let url = '/admin/api/2024-04/products.json?limit=250&fields=id,handle,title,images,options,variants,product_type,tags';

    // Paginate through all products using Link header
    while (url) {
      const resp = await fetch(proxyUrl + url, {
        headers: {
          'X-Shopify-Access-Token': token,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error('Shopify error ' + resp.status + ': ' + JSON.stringify(err.errors || ''));
      }
      const data = await resp.json();
      allProducts = allProducts.concat(data.products || []);

      // Parse Link header for next page
      const link = resp.headers.get('Link') || '';
      const nextMatch = link.match(/<([^>]+)>;\s*rel="next"/);
      if (nextMatch) {
        const nextFull = new URL(nextMatch[1]);
        url = nextFull.pathname + nextFull.search;
      } else {
        url = null;
      }

      if (btn) btn.textContent = '⟳ Syncing… (' + allProducts.length + ')';
    }

    // Convert to our compact catalog format.
    // pt / tg feed the Templates panel's type and tag filters; gid lets bulk
    // apply skip a per-product ID lookup (2 API calls saved per product).
    const catalog = allProducts.map(p => ({
      h: p.handle,
      t: p.title,
      i: (p.images && p.images[0]) ? p.images[0].src : '',
      o: (p.options && p.options[0]) ? p.options[0].name : 'Title',
      v: (p.variants || []).map(v => v.title).filter(t => t !== 'Default Title'),
      pt: p.product_type || '',
      tg: typeof p.tags === 'string' ? p.tags.split(',').map(t => t.trim()).filter(Boolean) : (p.tags || []),
      gid: String(p.id),
    }));

    // Merge into ALL_PRODUCTS — update existing, add new
    catalog.forEach(np => {
      const idx = ALL_PRODUCTS.findIndex(p => p.h === np.h);
      if (idx >= 0) ALL_PRODUCTS[idx] = np;
      else ALL_PRODUCTS.push(np);
    });

    filteredProds = [...ALL_PRODUCTS];

    // Persist the synced catalog to localStorage
    localStorage.setItem('ca_synced_products', JSON.stringify(catalog));
    localStorage.setItem('ca_synced_at', new Date().toISOString());

    renderProdList(filteredProds);
    document.getElementById('pcnt').textContent = ALL_PRODUCTS.length + ' products';
    updateSyncLabel();
    showToast('Synced ' + catalog.length + ' products ✓', 'success');
  } catch (e) {
    console.error('[Sync]', e);
    showToast('Sync failed: ' + e.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = '⟳ Sync'; }
  }
}

function updateSyncLabel() {
  const el = document.getElementById('sync-label');
  if (!el) return;
  const at = localStorage.getItem('ca_synced_at');
  if (at) {
    const d = new Date(at);
    el.textContent = 'Last sync: ' + d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'});
  }
}

// ── LOAD PRODUCT BY HANDLE ──
async function loadByHandle() {
  const inp = document.getElementById('handle-search-input');
  const handle = inp ? inp.value.trim().toLowerCase().replace(/\s+/g,'-') : '';
  if (!handle) return;
  if (ALL_PRODUCTS.find(p => p.h === handle)) {
    selectProd(handle);
    filterProds(handle);
    return;
  }
  showToast('Fetching product…', 'info');
  try {
    const res  = await fetch('https://chronoslights.com/products/' + handle + '.json');
    if (!res.ok) throw new Error('Product not found: ' + handle);
    const data = await res.json();
    const prod = data.product;
    const newP = {
      h: prod.handle,
      t: prod.title,
      i: (prod.images && prod.images[0]) ? prod.images[0].src : '',
      o: prod.options && prod.options[0] ? prod.options[0].name : 'Title',
      v: prod.variants ? prod.variants.map(v => v.title).filter(t => t !== 'Default Title') : [],
      pt: prod.product_type || '',
      tg: typeof prod.tags === 'string' ? prod.tags.split(',').map(t => t.trim()).filter(Boolean) : (prod.tags || []),
      gid: String(prod.id),
    };
    ALL_PRODUCTS.unshift(newP);
    filteredProds = [...ALL_PRODUCTS];
    const saved = JSON.parse(localStorage.getItem('ca_live_products') || '[]');
    if (!saved.find(p => p.h === newP.h)) { saved.unshift(newP); localStorage.setItem('ca_live_products', JSON.stringify(saved.slice(0,100))); }
    renderProdList(filteredProds);
    document.getElementById('pcnt').textContent = filteredProds.length + ' products';
    selectProd(prod.handle);
    showToast(prod.title + ' loaded!', 'success');
  } catch(e) {
    showToast(e.message, 'error');
  }
}

function filterProds(q) {
  q = q.toLowerCase().trim();
  filteredProds = q ? ALL_PRODUCTS.filter(p => p.h.includes(q) || p.t.toLowerCase().includes(q)) : [...ALL_PRODUCTS];
  document.getElementById('pcnt').textContent = filteredProds.length + ' / ' + ALL_PRODUCTS.length;
  renderProdList(filteredProds);
}

function renderProdList(list) {
  const el = document.getElementById('prod-list');
  // The templates storage product is infrastructure, not catalogue — filter it
  // out so it can't be selected or configured by accident. ID matches
  // STORAGE_PRODUCT_ID in chronos-templates.js.
  list = list.filter(p => String(p.gid || '') !== '10236050931960');
  if (!list.length) { el.innerHTML = '<div style="color:var(--muted);font-size:11px;padding:14px;text-align:center;">No products match</div>'; return; }
  el.innerHTML = list.map(p => {
    const hasCfg = !!localStorage.getItem('ca6_' + p.h);
    const isActive = selectedProd && selectedProd.h === p.h;
    const hasVolt = p.v && p.v.some(v => v.includes('12V') || v.includes('24V'));
    const imgHtml = p.i
      ? `<img class="pcard-img" src="${eh(p.i)}" onerror="this.style.display='none'">`
      : `<div class="pcard-img" style="display:flex;align-items:center;justify-content:center;font-size:14px;">🔌</div>`;
    const tags = [];
    if (hasCfg) tags.push('<span class="ptag cfg">Config ✓</span>');
    if (hasVolt) tags.push('<span class="ptag volt">⚡ voltage</span>');
    if (p.v && p.v.length > 1) tags.push(`<span class="ptag">${p.v.length} variants</span>`);
    return `<div class="pcard${isActive ? ' active' : ''}" onclick="selectProd('${eh(p.h)}')">
      ${imgHtml}
      <div class="pcard-info">
        <div class="pcard-name">${eh(p.t)}</div>
        <div class="pcard-handle">${eh(p.h)}</div>
        ${tags.length ? `<div class="pcard-tags">${tags.join('')}</div>` : ''}
      </div>
    </div>`;
  }).join('');
}

// ── SELECT PRODUCT + LIVE FETCH ──
async function selectProd(handle) {
  selectedProd = ALL_PRODUCTS.find(p => p.h === handle);
  if (!selectedProd) return;
  const saved = localStorage.getItem('ca6_' + handle);
  addonCfg = saved ? (() => { try { return JSON.parse(saved); } catch(e) { return {common:[], variant_rules:[]}; } })()
                   : {common:[], variant_rules:[]};
  if (!addonCfg.summary_labels) addonCfg.summary_labels = {};
  selGrp = null; dirty = false;
  bulkSelectedVariantId = null; bulkTiers = []; bulkBestQty = null; centerMode = 'addons';
  bulkApplyMode = false; bulkApplyBestQty = null; bulkApplySelected = new Set();
  document.getElementById('center-title').textContent = selectedProd.t.substring(0, 30) + (selectedProd.t.length > 30 ? '…' : '');
  document.getElementById('center-actions').removeAttribute('hidden');
  document.getElementById('save-bar').removeAttribute('hidden');
  document.getElementById('btn-save').disabled = false;
  renderProdList(filteredProds);
  document.getElementById('center-body').innerHTML = '<div class="loading-row"><div class="spinner"></div>Fetching variants…</div>';
  renderOptsEmpty();

  try {
    const res = await fetch('https://chronoslights.com/products/' + handle + '.json');
    if (!res.ok) throw new Error(res.status);
    const data = await res.json();
    const prod = data.product;
    fetchedVars = prod.variants.map(v => ({
      id: String(v.id), title: v.title, price: v.price,
      sku: v.sku || '', imageId: v.image_id ? String(v.image_id) : null,
      option1: v.option1, option2: v.option2
    }));
    fetchedImgs = (prod.images || []).map(i => ({id: String(i.id), src: i.src}));
    pickerCache[handle] = {variants: fetchedVars, images: fetchedImgs};
  } catch(e) {
    fetchedVars = (selectedProd.v || []).map((v, i) => ({id: String(i), title: v, price: '', sku: '', imageId: null, option1: v, option2: ''}));
    fetchedImgs = selectedProd.i ? [{id: '0', src: selectedProd.i}] : [];
    showToast('Using cached data — live fetch unavailable', 'info');
  }
  autoDetectRules();
  renderCenter();
  enablePushBtn();
}

function autoDetectRules() {
  const seen = {};
  fetchedVars.forEach(v => {
    const kw = extractVolt(v.title);
    if (kw && !seen[kw]) { seen[kw] = true; }
  });
  Object.keys(seen).forEach(kw => {
    // Check if any existing rule already covers this keyword
    const exists = addonCfg.variant_rules.some(r => {
      const tags = r.match_tags || (r.match ? [r.match] : []);
      return tags.some(t => t.toUpperCase() === kw.toUpperCase());
    });
    if (!exists) {
      addonCfg.variant_rules.push({
        match: kw,
        match_tags: [kw],
        groups: [],
        _auto: true
      });
    }
  });
}

function extractVolt(t) {
  const m = t.match(/\b(12V|24V|220V|5V|48V)\b/i);
  return m ? m[1].toUpperCase() : null;
}

// ── RENDER CENTER ──
function renderCenter() {
  const body = document.getElementById('center-body');
  let h = centerTabsHTML();
  h += (centerMode === 'bulk') ? bulkCenterHTML() : addonCenterHTML();
  body.innerHTML = h;
}

function centerTabsHTML() {
  return `<div class="ctab-strip">
    <button class="ctab${centerMode === 'addons' ? ' active' : ''}" onclick="setCenterMode('addons')">Addon builder</button>
    <button class="ctab${centerMode === 'bulk' ? ' active' : ''}" onclick="setCenterMode('bulk')">Bulk pricing</button>
  </div>`;
}

function setCenterMode(mode) {
  centerMode = mode;
  if (mode === 'bulk' && !bulkSelectedVariantId && fetchedVars.length) {
    selectBulkVariant(fetchedVars[0].id); // triggers its own renderCenter once loaded
    return;
  }
  renderCenter();
}

function addonCenterHTML() {
  let h = '';

  const pimg = fetchedImgs.length
    ? `<img class="prod-hdr-img" src="${eh(fetchedImgs[0].src)}" onerror="this.style.display='none'">`
    : `<div class="prod-hdr-img" style="display:flex;align-items:center;justify-content:center;font-size:20px;">🔌</div>`;
  const vpills = fetchedVars.map(v => {
    const kw = extractVolt(v.title);
    const c = kw === '12V' ? 'v12' : kw === '24V' ? 'v24' : kw ? 'vother' : '';
    return `<span class="vpill ${c}" title="ID:${eh(v.id)} Rs.${v.price}">${eh(v.title)}</span>`;
  }).join('');

  h += `<div class="prod-hdr">${pimg}
    <div style="flex:1;min-width:0;">
      <div class="prod-hdr-title">${eh(selectedProd.t)}</div>
      <div class="prod-hdr-handle">${eh(selectedProd.h)}</div>
      <div class="prod-hdr-variants">${vpills || '<span class="vpill">Single variant</span>'}</div>
    </div>
  </div>`;

  h += '<div class="ab">';

  // Per-product overrides for the three storefront price-summary rows.
  // Collapsed by default — most products keep the defaults, and this shouldn't
  // compete for attention with the group builder below it.
  const sl = addonCfg.summary_labels || {};
  h += `<details class="summary-labels">
    <summary>🏷 Price summary labels${(sl.base || sl.addons || sl.total) ? ' <span style="color:var(--accent);">(customized)</span>' : ''}</summary>
    <div class="summary-labels__body">
      <div style="font-size:10px;color:var(--muted);margin-bottom:8px;line-height:1.6;">
        Renames the three rows in the storefront price summary for this product. Leave blank to use the defaults.
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;">
        <div><div class="fl">Base row</div><input class="finput" value="${eh(sl.base || '')}" placeholder="Base price" oninput="updSummaryLabel('base',this.value)"></div>
        <div><div class="fl">Add-ons row</div><input class="finput" value="${eh(sl.addons || '')}" placeholder="Add-ons" oninput="updSummaryLabel('addons',this.value)"></div>
        <div><div class="fl">Total row</div><input class="finput" value="${eh(sl.total || '')}" placeholder="Total" oninput="updSummaryLabel('total',this.value)"></div>
      </div>
    </div>
  </details>`;

  h += '<div class="sec-hdr">Quick-Add Addon Group</div>';
  h += '<div class="cat-grid">';
  CATS.forEach(cat => {
    h += `<button class="cat-btn" onclick="openAGM('common','${cat.id}')">
      <span class="cat-ico">${cat.icon}</span>
      <div class="cat-lbl">${cat.label}</div>
      <div class="cat-sub">${cat.sub}</div>
    </button>`;
  });
  h += '</div>';

  h += `<div class="sec-hdr">Common Add-ons
    <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--muted);font-size:10px;">— all variants</span>
    <button class="btn btn-ghost btn-sm" onclick="openAGM('common','custom')" style="padding:2px 8px;font-size:10px;">+ Add</button>
  </div>`;

  if (!addonCfg.common.length) {
    h += '<div style="color:var(--muted);font-size:11px;padding:3px 0 10px;">No common groups yet — use Quick-Add above.</div>';
  }
  addonCfg.common.forEach((g, i) => {
    h += grpCardHTML(g, i, 'common', selGrp && selGrp.section === 'common' && selGrp.gIdx === i);
  });

  h += `<div class="sec-hdr" style="margin-top:6px;">Variant-Specific Add-ons
    <button class="btn btn-ghost btn-sm" onclick="addRuleManual()" style="padding:2px 8px;font-size:10px;">+ Rule</button>
  </div>`;

  if (!addonCfg.variant_rules.length) {
    h += '<div style="color:var(--muted);font-size:11px;padding:3px 0 10px;">No variant rules yet.</div>';
  }
  addonCfg.variant_rules.forEach((rule, rIdx) => {
    const tags = rule.match_tags || (rule.match ? [rule.match] : []);
    const kw = tags[0] || '';
    const tc = kw === '12V' ? 'v12' : kw === '24V' ? 'v24' : 'vother';
    const tagHtml = tags.map(t => `<span class="rtag ${tc}">${eh(t)}</span>`).join(' ');
    h += `<div class="rule-block">
      <div class="rule-hdr">
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
        Variant contains: ${tagHtml}
        ${rule._auto ? '<span style="font-size:9px;color:var(--info);margin-left:3px;">auto</span>' : ''}
        <div style="margin-left:auto;display:flex;gap:3px;">
          <button class="btn-icon" onclick="editRuleKw(${rIdx})">✏</button>
          <button class="btn-icon danger" onclick="deleteRule(${rIdx})">✕</button>
        </div>
      </div>
      <div class="rule-body">
        ${(rule.groups || []).map((g, gi) => grpCardHTML(g, gi, rIdx, selGrp && selGrp.section === rIdx && selGrp.gIdx === gi)).join('')}
        <div class="add-grp-row">
          ${CATS.slice(0, 5).map(cat => `<button class="add-grp-btn" onclick="openAGM(${rIdx},'${cat.id}')">${cat.icon} ${cat.label}</button>`).join('')}
          <button class="add-grp-btn" onclick="openAGM(${rIdx},'custom')">✏ Custom</button>
        </div>
      </div>
    </div>`;
  });

  h += '</div>';
  return h;
}

// ── BULK PRICING CENTER ──
function bulkCenterHTML() {
  const pimg = fetchedImgs.length
    ? `<img class="prod-hdr-img" src="${eh(fetchedImgs[0].src)}" onerror="this.style.display='none'">`
    : `<div class="prod-hdr-img" style="display:flex;align-items:center;justify-content:center;font-size:20px;">🔌</div>`;

  let h = `<div class="prod-hdr">${pimg}
    <div style="flex:1;min-width:0;">
      <div class="prod-hdr-title">${eh(selectedProd.t)}</div>
      <div class="prod-hdr-handle">${eh(selectedProd.h)}</div>
    </div>
  </div>`;

  // Clear ALL bulk pricing for this entire product — every variant,
  // regardless of which sub-tab or selection is active. Separate, red,
  // and its own confirmation since this is broader than either of the
  // other two clear actions below (which only touch the open/selected
  // variant(s)).
  h += `<div style="padding:10px 13px;border-bottom:1px solid var(--border);">
    <button class="btn btn-full btn-sm" id="btn-clear-all-bulk" onclick="clearAllBulkPricingForProduct()"
      style="background:rgba(224,85,85,.12);border:1px solid var(--danger);color:var(--danger);">
      🗑 Clear All Bulk Pricing for This Product
    </button>
    <div id="clear-all-bulk-status" style="font-size:11px;color:var(--muted);min-height:14px;margin-top:6px;text-align:center;"></div>
  </div>`;

  h += `<div style="display:flex;gap:6px;padding:10px 13px;border-bottom:1px solid var(--border);">
    <button class="btn btn-ghost btn-sm" style="${!bulkApplyMode ? 'border-color:var(--accent);color:var(--accent);' : ''}" onclick="setBulkApplyMode(false)">Single variant</button>
    <button class="btn btn-ghost btn-sm" style="${bulkApplyMode ? 'border-color:var(--accent);color:var(--accent);' : ''}" onclick="setBulkApplyMode(true)">Apply to multiple</button>
  </div>`;

  if (bulkApplyMode) return h + bulkApplyHTML();

  const vpills = fetchedVars.map(v => {
    const isSel = v.id === bulkSelectedVariantId;
    return `<span class="vpill bvpill${isSel ? ' bv-active' : ''}" onclick="selectBulkVariant('${eh(v.id)}')" title="ID:${eh(v.id)} Rs.${v.price}">${eh(v.title)}</span>`;
  }).join('');

  h += `<div class="ab"><div class="prod-hdr-variants" style="margin-bottom:10px;">${vpills || '<span class="vpill">Single variant</span>'}</div>`;

  if (!bulkSelectedVariantId) {
    h += '<div style="color:var(--muted);font-size:11px;padding:14px 0;">Click a variant pill above to edit its bulk pricing tiers.</div></div>';
    return h;
  }

  const v = fetchedVars.find(x => x.id === bulkSelectedVariantId);

  if (bulkLoadingVariant) {
    h += `<div class="sec-hdr">Bulk pricing — ${v ? eh(v.title) : ''}</div>`;
    h += '<div class="loading-row"><div class="spinner"></div>Loading tiers…</div></div>';
    return h;
  }

  h += `<div class="sec-hdr">Bulk pricing — ${v ? eh(v.title) : ''}</div>`;
  h += `<div style="font-size:11px;color:var(--muted);padding:6px 0 10px;line-height:1.6;">
    Tier 1 is always synced live from this variant's own Shopify price. Add tiers below for quantity
    breakpoints — each tier's price applies from that quantity upward until the next one. The star marks
    one tier as the "best seller / top choice" badge shown on the storefront.
  </div>`;
  h += `<div class="opt-hdr" style="grid-template-columns:1fr 1fr 28px 28px;"><div class="opt-col">Min qty</div><div class="opt-col">Price (Rs.)</div><div></div><div></div></div>`;
  h += `<div class="opt-row" style="padding:6px 9px;">
    <div style="display:grid;grid-template-columns:1fr 1fr 28px 28px;gap:5px;align-items:center;">
      <div style="font-size:12px;color:var(--muted);">1 (base)</div>
      <div style="font-size:12px;color:var(--muted);">Rs.${v ? eh(v.price) : '—'} (synced)</div>
      <div></div>
      <button class="star-btn${bulkBestQty === 1 ? ' on' : ''}" title="${bulkBestQty === 1 ? 'Top choice — click to unmark' : 'Mark as top choice'}" onclick="toggleBulkBest(1)">${bulkBestQty === 1 ? '★' : '☆'}</button>
    </div>
  </div>`;
  h += `<div id="bulk-tiers-list">${bulkTiersRowsHTML()}</div>`;
  h += `<button class="btn btn-ghost btn-sm" onclick="addBulkTier()" style="margin-top:6px;">+ Add tier</button>`;
  h += `<div style="margin-top:14px;display:flex;gap:6px;">
    <button class="btn btn-primary btn-full" onclick="saveBulkTiers()" id="btn-save-bulk">⬆ Save tiers to Shopify</button>
    <button class="btn btn-ghost" onclick="clearBulkPricingForCurrent()" title="Remove all bulk pricing from this variant" style="flex-shrink:0;">🗑 Clear</button>
  </div>
  <div id="bulk-save-status" style="font-size:11px;color:var(--muted);min-height:14px;margin-top:6px;"></div>`;

  h += '</div>';
  return h;
}

// ── BULK PRICING — APPLY TO MULTIPLE VARIANTS AT ONCE ──
function bulkApplyHTML() {
  let h = '<div class="ab">';
  h += '<div class="sec-hdr">Apply tiers to multiple variants</div>';
  h += `<div style="font-size:11px;color:var(--muted);padding:4px 0 10px;line-height:1.6;">
    Pick which variants to target and a pricing mode below, then save — repeat with a different group of
    variants and different numbers as many times as you like. Tier 1 always stays each variant's own
    Shopify price; everything here only fills tier 2 onward.
  </div>`;

  h += `<div style="display:flex;gap:6px;margin-bottom:12px;">
    <button class="btn btn-ghost btn-sm" style="${bulkApplyPricingMode === 'pct' ? 'border-color:var(--accent);color:var(--accent);' : ''}" onclick="setBulkApplyPricingMode('pct')">% off each variant's price</button>
    <button class="btn btn-ghost btn-sm" style="${bulkApplyPricingMode === 'fixed' ? 'border-color:var(--accent);color:var(--accent);' : ''}" onclick="setBulkApplyPricingMode('fixed')">Fixed Rs. price for the whole group</button>
  </div>`;

  const valColLabel = bulkApplyPricingMode === 'fixed' ? 'Price (Rs.)' : 'Discount %';
  h += `<div class="opt-hdr" style="grid-template-columns:1fr 1fr 28px 28px;"><div class="opt-col">Min qty</div><div class="opt-col">${valColLabel}</div><div></div><div></div></div>`;
  h += `<div id="bulk-apply-tiers-list">${bulkApplyTiersRowsHTML()}</div>`;
  h += `<button class="btn btn-ghost btn-sm" onclick="addBulkApplyTier()" style="margin-top:6px;">+ Add tier</button>`;

  h += '<div class="sec-hdr" style="margin-top:18px;">Apply to which variants?</div>';
  h += `<div style="display:flex;gap:6px;margin:8px 0;">
    <button class="btn btn-ghost btn-sm" onclick="bulkApplySelectAll()">Select all (${fetchedVars.length})</button>
    <button class="btn btn-ghost btn-sm" onclick="bulkApplySelectNone()">Deselect all</button>
  </div>`;
  h += `<div id="bulk-apply-vlist" style="display:flex;flex-wrap:wrap;gap:6px;">${bulkApplyVariantPillsHTML()}</div>`;

  h += `<div style="margin-top:16px;display:flex;gap:6px;">
    <button class="btn btn-primary btn-full" onclick="applyBulkTiersToSelected()" id="btn-bulk-apply">⬆ Apply &amp; save to ${bulkApplySelected.size} variant${bulkApplySelected.size === 1 ? '' : 's'}</button>
    <button class="btn btn-ghost" onclick="clearBulkPricingForSelected()" id="btn-bulk-clear" title="Remove all bulk pricing from the selected variants" style="flex-shrink:0;">🗑 Clear selected</button>
  </div>
  <div id="bulk-apply-status" style="font-size:11px;color:var(--muted);min-height:14px;margin-top:6px;"></div>`;

  h += '</div>';
  return h;
}

function setBulkApplyPricingMode(mode) {
  if (mode === bulkApplyPricingMode) return;
  bulkApplyPricingMode = mode;
  // Reset tier values rather than leaving numbers typed under the old mode
  // (a "5" meant 5% — leaving it in place under Fixed Rs mode would silently
  // become Rs.5, which is exactly the kind of mismatch this whole tool exists
  // to prevent) — so each mode switch starts from a sensible, mode-correct default.
  bulkApplyTiers = mode === 'fixed' ? [{ q: 25, val: 0 }] : [{ q: 10, val: 5 }];
  bulkApplyBestQty = null;
  renderCenter();
}

function bulkApplyTiersRowsHTML() {
  if (!bulkApplyTiers.length) {
    return '<div style="color:var(--muted);font-size:11px;padding:6px 0;">No tiers yet — click "+ Add tier" below.</div>';
  }
  const isFixed = bulkApplyPricingMode === 'fixed';
  return bulkApplyTiers.map((t, i) => `
    <div class="opt-row" style="padding:6px 9px;">
      <div style="display:grid;grid-template-columns:1fr 1fr 28px 28px;gap:5px;align-items:center;">
        <input type="number" value="${eh(t.q)}" min="1" oninput="updBulkApplyTier(${i},'q',this.value)">
        <input type="number" value="${eh(t.val)}" min="0" ${isFixed ? 'step="0.01"' : 'max="90" step="0.1"'} oninput="updBulkApplyTier(${i},'val',this.value)">
        <button class="del-x" onclick="delBulkApplyTier(${i})">
          <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
        <button class="star-btn${bulkApplyBestQty === t.q ? ' on' : ''}" title="${bulkApplyBestQty === t.q ? 'Top choice — click to unmark' : 'Mark as top choice'}" onclick="toggleBulkApplyBest(${t.q})">${bulkApplyBestQty === t.q ? '★' : '☆'}</button>
      </div>
    </div>`).join('');
}

function toggleBulkApplyBest(qty) {
  bulkApplyBestQty = (bulkApplyBestQty === qty) ? null : qty;
  const list = document.getElementById('bulk-apply-tiers-list');
  if (list) list.innerHTML = bulkApplyTiersRowsHTML();
}

function updBulkApplyTier(i, k, v) {
  if (!bulkApplyTiers[i]) return;
  bulkApplyTiers[i][k] = parseFloat(v) || 0;
}

function addBulkApplyTier() {
  bulkApplyTiers.push({ q: 0, val: 0 });
  const list = document.getElementById('bulk-apply-tiers-list');
  if (list) list.innerHTML = bulkApplyTiersRowsHTML();
}

function delBulkApplyTier(i) {
  bulkApplyTiers.splice(i, 1);
  const list = document.getElementById('bulk-apply-tiers-list');
  if (list) list.innerHTML = bulkApplyTiersRowsHTML();
}

function bulkApplyVariantPillsHTML() {
  return fetchedVars.map(v => {
    const sel = bulkApplySelected.has(v.id);
    return `<span class="vpill bvpill${sel ? ' bv-active' : ''}" onclick="toggleBulkApplyVariant('${eh(v.id)}')" title="Rs.${eh(v.price)}">${eh(v.title)}</span>`;
  }).join('');
}

function toggleBulkApplyVariant(id) {
  if (bulkApplySelected.has(id)) bulkApplySelected.delete(id);
  else bulkApplySelected.add(id);
  const list = document.getElementById('bulk-apply-vlist');
  if (list) list.innerHTML = bulkApplyVariantPillsHTML();
  updateBulkApplyBtnLabel();
}

function bulkApplySelectAll() {
  fetchedVars.forEach(v => bulkApplySelected.add(v.id));
  const list = document.getElementById('bulk-apply-vlist');
  if (list) list.innerHTML = bulkApplyVariantPillsHTML();
  updateBulkApplyBtnLabel();
}

function bulkApplySelectNone() {
  bulkApplySelected.clear();
  const list = document.getElementById('bulk-apply-vlist');
  if (list) list.innerHTML = bulkApplyVariantPillsHTML();
  updateBulkApplyBtnLabel();
}

function updateBulkApplyBtnLabel() {
  const btn = document.getElementById('btn-bulk-apply');
  if (btn) btn.textContent = '⬆ Apply & save to ' + bulkApplySelected.size + ' variant' + (bulkApplySelected.size === 1 ? '' : 's');
}

function setBulkApplyMode(on) {
  bulkApplyMode = on;
  renderCenter();
}

async function applyBulkTiersToSelected() {
  if (!bulkApplySelected.size) { showToast('Select at least one variant', 'error'); return; }
  if (!bulkApplyTiers.length) { showToast('Add at least one tier', 'error'); return; }

  const btn      = document.getElementById('btn-bulk-apply');
  const statusEl = document.getElementById('bulk-apply-status');
  if (btn) btn.disabled = true;

  const sortedTiers = [...bulkApplyTiers].sort((a, b) => a.q - b.q);
  const qArr = sortedTiers.map(t => Math.round(t.q));

  const targets = Array.from(bulkApplySelected);
  let done = 0;

  for (const variantId of targets) {
    const v = fetchedVars.find(x => x.id === variantId);
    // Decimal-formatted strings — Shopify's list.number_decimal rejects plain numbers
    const pArr = bulkApplyPricingMode === 'fixed'
      ? sortedTiers.map(t => Number(t.val).toFixed(2))                                    // same Rs price for every variant in this group
      : sortedTiers.map(t => ((v ? parseFloat(v.price) : 0) * (1 - t.val / 100)).toFixed(2)); // % off this variant's own price

    if (statusEl) statusEl.textContent = 'Saving ' + (done + 1) + ' / ' + targets.length + '… (' + (v ? v.title : variantId) + ')';
    try {
      await saveVariantMetafield(variantId, 'bulk_discount_quantity', 'list.number_integer', qArr);
      await saveVariantMetafield(variantId, 'bulk_discount_price', 'list.number_decimal', pArr);
      await saveOrClearScalarVariantMetafield(variantId, 'bulk_discount_best_qty', 'number_integer', bulkApplyBestQty);
      done++;
    } catch (e) {
      console.error('[BulkApply] failed for', variantId, e);
      showToast('Failed on ' + (v ? v.title : variantId) + ': ' + e.message, 'error');
    }
  }

  if (statusEl) {
    statusEl.style.color = done === targets.length ? 'var(--success)' : 'var(--danger)';
    statusEl.textContent = 'Applied to ' + done + ' / ' + targets.length + ' variants' + (done === targets.length ? ' ✓' : '');
  }
  showToast('Bulk apply complete — ' + done + '/' + targets.length + ' saved', done === targets.length ? 'success' : 'error');
  if (btn) btn.disabled = false;

  // If the single-variant editor currently has one of the just-updated variants open, refresh it
  if (bulkSelectedVariantId && bulkApplySelected.has(bulkSelectedVariantId)) {
    loadBulkTiers(bulkSelectedVariantId);
  }
}

// Clears bulk pricing from just the variants ticked in "Apply to multiple".
async function clearBulkPricingForSelected() {
  if (!bulkApplySelected.size) { showToast('Select at least one variant', 'error'); return; }
  if (!confirm('Remove all bulk pricing tiers from ' + bulkApplySelected.size + ' selected variant(s)? This deletes their saved data on Shopify.')) return;

  const btn      = document.getElementById('btn-bulk-clear');
  const statusEl = document.getElementById('bulk-apply-status');
  if (btn) btn.disabled = true;

  const targets = Array.from(bulkApplySelected);
  let done = 0;

  for (const variantId of targets) {
    const v = fetchedVars.find(x => x.id === variantId);
    if (statusEl) statusEl.textContent = 'Clearing ' + (done + 1) + ' / ' + targets.length + '… (' + (v ? v.title : variantId) + ')';
    try {
      await deleteVariantMetafieldIfExists(variantId, 'bulk_discount_quantity');
      await deleteVariantMetafieldIfExists(variantId, 'bulk_discount_price');
      await deleteVariantMetafieldIfExists(variantId, 'bulk_discount_best_qty');
      done++;
    } catch (e) {
      console.error('[BulkClear] failed for', variantId, e);
      showToast('Failed on ' + (v ? v.title : variantId) + ': ' + e.message, 'error');
    }
  }

  if (statusEl) {
    statusEl.style.color = done === targets.length ? 'var(--success)' : 'var(--danger)';
    statusEl.textContent = 'Cleared ' + done + ' / ' + targets.length + ' variants' + (done === targets.length ? ' ✓' : '');
  }
  showToast('Bulk clear complete — ' + done + '/' + targets.length + ' cleared', done === targets.length ? 'success' : 'error');
  if (btn) btn.disabled = false;

  if (bulkSelectedVariantId && bulkApplySelected.has(bulkSelectedVariantId)) {
    loadBulkTiers(bulkSelectedVariantId);
  }
}

// Clears bulk pricing (quantity tiers, prices, and the best/top-choice marker)
// from EVERY variant of the currently selected product — not just the one open
// in the single-variant editor, and not just whichever variants happen to be
// ticked in "Apply to multiple". This is the broadest of the three clear
// actions, hence the separate red button and its own confirmation copy naming
// the product and variant count.
async function clearAllBulkPricingForProduct() {
  if (!selectedProd || !fetchedVars.length) return;
  if (!confirm(
    'Remove ALL bulk pricing tiers from EVERY variant of "' + selectedProd.t + '"?\n\n' +
    'This clears ' + fetchedVars.length + ' variant' + (fetchedVars.length === 1 ? '' : 's') +
    ' — including any variant not currently open or selected — and deletes their saved data on Shopify. This cannot be undone.'
  )) return;

  const btn      = document.getElementById('btn-clear-all-bulk');
  // Falls back to the Danger zone's status line when invoked from there
  // (the bulk tab's own status element only exists on that tab).
  const statusEl = document.getElementById('clear-all-bulk-status')
                || document.getElementById('danger-status');
  if (btn) btn.disabled = true;
  setDangerBtnsDisabled(true);

  let done = 0;
  const total = fetchedVars.length;

  for (const v of fetchedVars) {
    if (statusEl) statusEl.textContent = 'Clearing ' + (done + 1) + ' / ' + total + '… (' + v.title + ')';
    try {
      await deleteVariantMetafieldIfExists(v.id, 'bulk_discount_quantity');
      await deleteVariantMetafieldIfExists(v.id, 'bulk_discount_price');
      await deleteVariantMetafieldIfExists(v.id, 'bulk_discount_best_qty');
      done++;
    } catch (e) {
      console.error('[ClearAllBulk] failed for', v.id, e);
      showToast('Failed on ' + v.title + ': ' + e.message, 'error');
    }
  }

  if (statusEl) {
    statusEl.style.color = done === total ? 'var(--success)' : 'var(--danger)';
    statusEl.textContent = 'Cleared ' + done + ' / ' + total + ' variants' + (done === total ? ' ✓' : '');
  }
  showToast('Cleared bulk pricing on ' + done + '/' + total + ' variants for this product', done === total ? 'success' : 'error');
  if (btn) btn.disabled = false;
  setDangerBtnsDisabled(false);

  // Reset in-memory state and refresh whatever's currently on screen
  bulkTiers = [];
  bulkBestQty = null;
  bulkApplySelected = new Set();
  if (bulkSelectedVariantId) {
    loadBulkTiers(bulkSelectedVariantId);
  } else {
    renderCenter();
  }
}

function bulkTiersRowsHTML() {
  if (!bulkTiers.length) {
    return '<div style="color:var(--muted);font-size:11px;padding:6px 0;">No tiers yet — click "+ Add tier" below.</div>';
  }
  return bulkTiers.map((t, i) => `
    <div class="opt-row" style="padding:6px 9px;">
      <div style="display:grid;grid-template-columns:1fr 1fr 28px 28px;gap:5px;align-items:center;">
        <input type="number" value="${eh(t.q)}" min="1" oninput="updBulkTier(${i},'q',this.value)">
        <input type="number" value="${eh(t.p)}" min="0" step="0.01" oninput="updBulkTier(${i},'p',this.value)">
        <button class="del-x" onclick="delBulkTier(${i})">
          <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
        <button class="star-btn${bulkBestQty === t.q ? ' on' : ''}" title="${bulkBestQty === t.q ? 'Top choice — click to unmark' : 'Mark as top choice'}" onclick="toggleBulkBest(${t.q})">${bulkBestQty === t.q ? '★' : '☆'}</button>
      </div>
    </div>`).join('');
}

function toggleBulkBest(qty) {
  bulkBestQty = (bulkBestQty === qty) ? null : qty;
  renderCenter();
}

function updBulkTier(i, k, v) {
  if (!bulkTiers[i]) return;
  bulkTiers[i][k] = parseFloat(v) || 0;
}

function addBulkTier() {
  bulkTiers.push({ q: 0, p: 0 });
  const list = document.getElementById('bulk-tiers-list');
  if (list) list.innerHTML = bulkTiersRowsHTML();
}

function delBulkTier(i) {
  bulkTiers.splice(i, 1);
  const list = document.getElementById('bulk-tiers-list');
  if (list) list.innerHTML = bulkTiersRowsHTML();
}

async function selectBulkVariant(variantId) {
  bulkSelectedVariantId = variantId;
  await loadBulkTiers(variantId);
}

async function loadBulkTiers(variantId) {
  bulkLoadingVariant = true;
  renderCenter();

  const token    = (localStorage.getItem('ca_shpat') || '').trim();
  const proxyUrl = (localStorage.getItem('ca_proxy_url') || '').trim();
  if (!token || !proxyUrl) {
    bulkTiers = [];
    bulkLoadingVariant = false;
    renderCenter();
    showToast('Add API token + Proxy URL in Settings first', 'error');
    openProxySettings();
    return;
  }

  try {
    const [qRes, pRes, bRes] = await Promise.all([
      shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json?namespace=custom&key=bulk_discount_quantity', { method: 'GET' }),
      shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json?namespace=custom&key=bulk_discount_price', { method: 'GET' }),
      shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json?namespace=custom&key=bulk_discount_best_qty', { method: 'GET' }),
    ]);
    if (!qRes.ok || !pRes.ok || !bRes.ok) throw new Error('Lookup failed (' + qRes.status + '/' + pRes.status + '/' + bRes.status + ') — check API scopes in Settings');

    const qData = await qRes.json();
    const pData = await pRes.json();
    const bData = await bRes.json();
    const qMeta = (qData.metafields || [])[0];
    const pMeta = (pData.metafields || [])[0];
    const bMeta = (bData.metafields || [])[0];
    const qArr  = qMeta ? (typeof qMeta.value === 'string' ? JSON.parse(qMeta.value) : qMeta.value) : [];
    const pArr  = pMeta ? (typeof pMeta.value === 'string' ? JSON.parse(pMeta.value) : pMeta.value) : [];

    bulkTiers   = qArr.map((q, i) => ({ q: q, p: pArr[i] != null ? pArr[i] : 0 }));
    bulkBestQty = bMeta ? parseInt(bMeta.value, 10) : null;
  } catch (e) {
    console.error('[BulkPricing] load failed', e);
    bulkTiers = [];
    bulkBestQty = null;
    showToast('Could not load tiers: ' + e.message, 'error');
  } finally {
    bulkLoadingVariant = false;
    renderCenter();
  }
}

async function saveBulkTiers() {
  console.group('[DEBUG saveBulkTiers] STARTING');
  console.log('[DEBUG] bulkSelectedVariantId:', bulkSelectedVariantId);
  console.log('[DEBUG] bulkTiers:', bulkTiers);
  console.log('[DEBUG] bulkApplyMode:', bulkApplyMode);
  console.log('[DEBUG] bulkApplySelected.size:', bulkApplySelected.size);
  
  if (!bulkSelectedVariantId && !bulkApplyMode) {
    console.error('[DEBUG] EXIT: No variant selected and not in apply mode');
    console.groupEnd();
    return;
  }
  const btn       = document.getElementById('btn-save-bulk');
  const statusEl  = document.getElementById('bulk-save-status');
  if (btn) btn.disabled = true;
  if (statusEl) { statusEl.style.color = 'var(--muted)'; statusEl.textContent = 'Saving…'; }

  // Sort by quantity ascending so tier order is always sane regardless of entry order.
  // Price values must be sent as decimal-formatted STRINGS ("58.00", not 58) —
  // Shopify's list.number_decimal type rejects plain numbers with "must be a decimal."
  const sorted = [...bulkTiers].sort((a, b) => a.q - b.q);
  const qArr = sorted.map(t => Math.round(t.q));
  const pArr = sorted.map(t => Number(t.p).toFixed(2));

  try {
    console.log('[DEBUG] Calling saveVariantMetafield for quantity...');
    await saveVariantMetafield(bulkSelectedVariantId, 'bulk_discount_quantity', 'list.number_integer', qArr);
    console.log('[DEBUG] ✓ Quantity metafield saved');
    
    console.log('[DEBUG] Calling saveVariantMetafield for price...');
    await saveVariantMetafield(bulkSelectedVariantId, 'bulk_discount_price', 'list.number_decimal', pArr);
    console.log('[DEBUG] ✓ Price metafield saved');
    
    console.log('[DEBUG] Calling saveOrClearScalarVariantMetafield for best_qty...');
    await saveOrClearScalarVariantMetafield(bulkSelectedVariantId, 'bulk_discount_best_qty', 'number_integer', bulkBestQty);
    console.log('[DEBUG] ✓ Best qty metafield saved');
    
    bulkTiers = sorted;
    console.log('[DEBUG] ✓ All metafields saved successfully!');
    console.groupEnd();
    
    if (statusEl) { statusEl.style.color = 'var(--success)'; statusEl.textContent = 'Saved ✓'; }
    showToast('Bulk pricing tiers saved ✓', 'success');
    renderCenter();
  } catch (e) {
    console.error('[DEBUG] ✗ saveBulkTiers FAILED:', e);
    console.groupEnd();
    if (statusEl) { statusEl.style.color = 'var(--danger)'; statusEl.textContent = e.message; }
    showToast('Save failed', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function clearBulkPricingForCurrent() {
  if (!bulkSelectedVariantId) return;
  if (!confirm('Remove all bulk pricing tiers from this variant? This deletes its saved data on Shopify.')) return;

  const statusEl = document.getElementById('bulk-save-status');
  if (statusEl) { statusEl.style.color = 'var(--muted)'; statusEl.textContent = 'Clearing…'; }

  try {
    await deleteVariantMetafieldIfExists(bulkSelectedVariantId, 'bulk_discount_quantity');
    await deleteVariantMetafieldIfExists(bulkSelectedVariantId, 'bulk_discount_price');
    await deleteVariantMetafieldIfExists(bulkSelectedVariantId, 'bulk_discount_best_qty');
    bulkTiers = [];
    bulkBestQty = null;
    showToast('Bulk pricing cleared for this variant', 'success');
    renderCenter();
  } catch (e) {
    console.error('[BulkPricing] clear failed', e);
    showToast('Clear failed: ' + e.message, 'error');
    if (statusEl) { statusEl.style.color = 'var(--danger)'; statusEl.textContent = e.message; }
  }
}

async function saveVariantMetafield(variantId, key, type, valueArr) {
  const getRes = await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json?namespace=custom&key=' + key, { method: 'GET' });
  if (!getRes.ok) throw new Error('Lookup failed for ' + key + ' (' + getRes.status + ')');
  const getData  = await getRes.json();
  const existing = (getData.metafields || [])[0];

  const body = JSON.stringify({
    metafield: existing
      ? { id: existing.id, value: JSON.stringify(valueArr), type: type }
      : { namespace: 'custom', key: key, value: JSON.stringify(valueArr), type: type }
  });

  const saveRes = existing
    ? await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields/' + existing.id + '.json', { method: 'PUT', body })
    : await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json', { method: 'POST', body });

  if (!saveRes.ok) {
    const errJson = await saveRes.json().catch(function() { return {}; });
    throw new Error(JSON.stringify(errJson.errors || saveRes.status));
  }

  // Catch Shopify body-level errors even on HTTP 200/201
  const saveData = await saveRes.json().catch(function() { return {}; });
  if (saveData.errors) {
    throw new Error('Shopify rejected ' + key + ': ' + JSON.stringify(saveData.errors));
  }

  // Confirms the metafield landed on a variant, not a product
  const saved = saveData.metafield;
  if (saved) {
    console.log('[saveVariantMetafield] saved', key, '→ owner_id:', saved.owner_id, 'owner_resource:', saved.owner_resource, 'id:', saved.id);
  }
}

// Saves a single scalar value (e.g. the "best tier" quantity marker). Passing
// value === null deletes the metafield instead of writing it — used when a
// tier is unmarked, so no stale "best" marker is left behind on Shopify.
async function saveOrClearScalarVariantMetafield(variantId, key, type, value) {
  if (value === null || value === undefined) {
    await deleteVariantMetafieldIfExists(variantId, key);
    return;
  }

  const getRes = await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json?namespace=custom&key=' + key, { method: 'GET' });
  if (!getRes.ok) throw new Error('Lookup failed for ' + key + ' (' + getRes.status + ')');
  const getData  = await getRes.json();
  const existing = (getData.metafields || [])[0];

  const body = JSON.stringify({
    metafield: existing
      ? { id: existing.id, value: String(value), type: type }
      : { namespace: 'custom', key: key, value: String(value), type: type }
  });

  const saveRes = existing
    ? await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields/' + existing.id + '.json', { method: 'PUT', body })
    : await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json', { method: 'POST', body });

  if (!saveRes.ok) {
    const errJson = await saveRes.json().catch(function() { return {}; });
    throw new Error(JSON.stringify(errJson.errors || saveRes.status));
  }
}

// Used by all the "Clear" buttons to wipe any of the three bulk-pricing
// metafields from a variant if it exists.
async function deleteVariantMetafieldIfExists(variantId, key) {
  const getRes = await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields.json?namespace=custom&key=' + key, { method: 'GET' });
  if (!getRes.ok) throw new Error('Lookup failed for ' + key + ' (' + getRes.status + ')');
  const getData  = await getRes.json();
  const existing = (getData.metafields || [])[0];
  if (!existing) return;

  const delRes = await shopifyFetch('/admin/api/2024-04/variants/' + variantId + '/metafields/' + existing.id + '.json', { method: 'DELETE' });
  if (!delRes.ok) {
    const errJson = await delRes.json().catch(function() { return {}; });
    throw new Error(JSON.stringify(errJson.errors || delRes.status));
  }
}

// ── PRODUCT-LEVEL CLEAR ACTIONS ───────────────────────────────────────────
// Three destructive actions, all scoped to the currently selected product.
// Add-ons live in ONE product metafield (custom.addon_groups); bulk pricing
// lives in THREE metafields per VARIANT. They're independent — clearing one
// never touches the other — which is why "Clear everything" exists as a
// separate action rather than being implied by either.

function dangerStatus(msg, type) {
  const el = document.getElementById('danger-status');
  if (!el) return;
  el.style.color = type === 'success' ? 'var(--success)'
                 : type === 'error'   ? 'var(--danger)'
                 : 'var(--muted)';
  el.textContent = msg;
}

function setDangerBtnsDisabled(on) {
  document.querySelectorAll('.danger-zone__body .btn').forEach(b => { b.disabled = on; });
}

// Deletes the product's custom.addon_groups metafield on Shopify, plus the
// local copy that drives the "Config ✓" tag and the Configured list. Returns
// true on success so clearEverythingForProduct can chain it.
async function clearAddonsForProduct(skipConfirm) {
  if (!selectedProd) { showToast('Select a product first', 'error'); return false; }
  if (!skipConfirm && !confirm(
    'Remove ALL add-on groups from "' + selectedProd.t + '"?\n\n' +
    'This deletes the custom.addon_groups metafield on Shopify and the saved copy ' +
    'on this browser. Bulk pricing is not affected. This cannot be undone.'
  )) return false;

  setDangerBtnsDisabled(true);
  dangerStatus('Looking up product…', 'info');

  try {
    const r1 = await shopifyFetch('/admin/api/2024-04/products.json?handle=' + encodeURIComponent(selectedProd.h) + '&fields=id', { method: 'GET' });
    if (!r1.ok) throw new Error('Lookup failed (' + r1.status + ') — check API scopes in Settings');
    const d1  = await r1.json();
    const pid = (d1.products || [])[0] && d1.products[0].id;
    if (!pid) throw new Error('Product not found: ' + selectedProd.h);

    dangerStatus('Checking metafield…', 'info');
    const r2   = await shopifyFetch('/admin/api/2024-04/products/' + pid + '/metafields.json?namespace=custom&key=addon_groups', { method: 'GET' });
    if (!r2.ok) throw new Error('Metafield lookup failed (' + r2.status + ')');
    const d2   = await r2.json();
    const exst = (d2.metafields || []).filter(m => m.namespace === 'custom' && m.key === 'addon_groups')[0];

    if (exst) {
      dangerStatus('Deleting…', 'info');
      const r3 = await shopifyFetch('/admin/api/2024-04/products/' + pid + '/metafields/' + exst.id + '.json', { method: 'DELETE' });
      if (!r3.ok) {
        const e = await r3.json().catch(() => ({}));
        throw new Error(JSON.stringify(e.errors || r3.status));
      }
    }

    // Local cleanup — without this the product keeps its "Config ✓" tag and
    // stays in the Configured list even though Shopify no longer has a config.
    localStorage.removeItem('ca6_' + selectedProd.h);
    try {
      const cached = JSON.parse(localStorage.getItem('ca_shopify_configured') || '[]');
      localStorage.setItem('ca_shopify_configured', JSON.stringify(cached.filter(p => p.h !== selectedProd.h)));
    } catch (e) {}

    // Reset in-memory state and repaint
    addonCfg = { common: [], variant_rules: [], summary_labels: {} };
    selGrp = null;
    dirty = false;
    autoDetectRules();
    renderCenter();
    renderOptsEmpty();
    renderProdList(filteredProds);
    loadConfiguredCache();
    updateConfiguredLabel();

    dangerStatus(exst ? 'Add-ons cleared ✓' : 'No add-on config existed — local copy cleared ✓', 'success');
    showToast('Add-ons cleared for this product', 'success');
    return true;
  } catch (e) {
    console.error('[ClearAddons]', e);
    dangerStatus(e.message, 'error');
    showToast('Clear failed: ' + e.message, 'error');
    return false;
  } finally {
    setDangerBtnsDisabled(false);
  }
}

// Clears both add-ons and bulk pricing in one pass, with a single confirmation
// naming everything that's about to go.
async function clearEverythingForProduct() {
  if (!selectedProd || !fetchedVars.length) { showToast('Select a product first', 'error'); return; }
  if (!confirm(
    'Remove EVERYTHING from "' + selectedProd.t + '"?\n\n' +
    '• All add-on groups (product metafield + local copy)\n' +
    '• All bulk pricing tiers on all ' + fetchedVars.length + ' variant' + (fetchedVars.length === 1 ? '' : 's') + '\n\n' +
    'This cannot be undone.'
  )) return;

  setDangerBtnsDisabled(true);
  let addonsOk = false, bulkDone = 0;
  const total = fetchedVars.length;

  try {
    dangerStatus('Clearing add-ons…', 'info');
    addonsOk = await clearAddonsForProduct(true);

    for (const v of fetchedVars) {
      dangerStatus('Clearing bulk pricing ' + (bulkDone + 1) + ' / ' + total + '… (' + v.title + ')', 'info');
      try {
        await deleteVariantMetafieldIfExists(v.id, 'bulk_discount_quantity');
        await deleteVariantMetafieldIfExists(v.id, 'bulk_discount_price');
        await deleteVariantMetafieldIfExists(v.id, 'bulk_discount_best_qty');
        bulkDone++;
      } catch (e) {
        console.error('[ClearEverything] bulk failed for', v.id, e);
      }
    }

    bulkTiers = [];
    bulkBestQty = null;
    bulkApplySelected = new Set();
    renderCenter();

    const allOk = addonsOk && bulkDone === total;
    dangerStatus(
      'Add-ons: ' + (addonsOk ? 'cleared ✓' : 'FAILED') +
      ' · Bulk pricing: ' + bulkDone + '/' + total + (bulkDone === total ? ' ✓' : ' — some failed'),
      allOk ? 'success' : 'error'
    );
    showToast(allOk ? 'Product fully cleared ✓' : 'Cleared with some failures — see status', allOk ? 'success' : 'error');
  } finally {
    setDangerBtnsDisabled(false);
  }
}

function grpCardHTML(g, gi, section, isActive) {
  const sStr = typeof section === 'number' ? section : `'${section}'`;
  const hasImg = (g.options || []).some(o => o.image);
  return `<div class="grp-card${isActive ? ' active' : ''}"
       draggable="true"
       data-section="${eh(String(section))}"
       data-gi="${gi}"
       ondragstart="onGrpDragStart(event,${sStr},${gi})"
       ondragover="onGrpDragOver(event,${sStr},${gi})"
       ondrop="onGrpDrop(event,${sStr},${gi})"
       ondragend="onGrpDragEnd(event)"
       onclick="selectGrp(${sStr},${gi})">
    <div class="grp-head">
      <span class="drag-handle" title="Drag to reorder" onclick="event.stopPropagation()">≡</span>
      <div class="grp-num">${gi + 1}</div>
      <span class="grp-lbl">${eh(g.label || 'Untitled')}${g.required ? ' <span style="font-size:9px;color:var(--success);">(req)</span>' : ''}${g.replaces_main ? ' <span style="font-size:9px;color:var(--info);">(replaces main)</span>' : ''}</span>
      ${hasImg ? '<div class="img-dot" title="Has image URLs"></div>' : ''}
      <span class="grp-typetag">${g.display || 'button'}</span>
      <button class="del-x" onclick="event.stopPropagation();deleteGrp(${sStr},${gi})">
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
    </div>
  </div>`;
}

// ── ADD GROUP MODAL ──
function openAGM(section, catId) {
  agmSection = section; agmCatId = catId; scopeMode = 'all'; scopeSel = new Set();
  const cat = CATS.find(c => c.id === catId) || CATS[CATS.length - 1];
  document.getElementById('agm-title').textContent = 'Add ' + cat.label + ' Group';
  document.getElementById('agm-sub').textContent   = cat.sub + ' — choose scope below';
  const ruleKw = typeof section === 'number' ? (addonCfg.variant_rules[section]?.match || '') : '';
  document.getElementById('agm-label').value   = ruleKw ? cat.label + ' (' + ruleKw + ')' : cat.label;
  document.getElementById('agm-id').value      = catId + (ruleKw ? '_' + ruleKw.toLowerCase() : '');
  document.getElementById('agm-display').value = cat.display;
  if (typeof section === 'number') {
    setScope('sel');
    const tags = addonCfg.variant_rules[section]?.match_tags || [];
    fetchedVars.forEach(v => { if (tags.some(t => v.title.toUpperCase().includes(t.toUpperCase()))) scopeSel.add(v.title); });
    renderVsel();
  } else {
    setScope('all');
  }
  document.getElementById('agm-overlay').classList.add('show');
}

function setScope(mode) {
  scopeMode = mode;
  document.getElementById('sb-all').className = 'scope-btn' + (mode === 'all' ? ' on' : '');
  document.getElementById('sb-sel').className = 'scope-btn' + (mode === 'sel' ? ' on' : '');
  document.getElementById('scope-vsel-wrap').style.display = mode === 'sel' ? '' : 'none';
  if (mode === 'sel') renderVsel();
}

function renderVsel() {
  document.getElementById('scope-vsel-grid').innerHTML = fetchedVars.map(v => {
    const kw = extractVolt(v.title);
    const c = kw === '12V' ? 'v12' : kw === '24V' ? 'v24' : '';
    const on = scopeSel.has(v.title);
    return `<div class="vsel-item${on ? ' on ' + c : ''}" onclick="toggleVsel('${eh(v.title)}',this,'${c}')">
      ${eh(v.title)}
      ${v.price ? `<br><span style="font-size:9px;color:var(--muted);">Rs.${v.price}</span>` : ''}
    </div>`;
  }).join('');
}

function toggleVsel(t, el, c) {
  if (scopeSel.has(t)) { scopeSel.delete(t); el.classList.remove('on', c); }
  else { scopeSel.add(t); el.classList.add('on', c); }
}

function confirmAddGroup() {
  const label   = document.getElementById('agm-label').value.trim() || 'New Group';
  const id      = document.getElementById('agm-id').value.trim() || ('grp_' + Date.now());
  const display = document.getElementById('agm-display').value;
  const newGrp  = {id, label, display, options: [{label: 'No ' + label, handle: ''}]};

  if (scopeMode === 'all' || !scopeSel.size) {
    addonCfg.common.push(newGrp);
    selGrp = {section: 'common', gIdx: addonCfg.common.length - 1};
  } else {
    const volGroups = {};
    scopeSel.forEach(vt => {
      const kw = extractVolt(vt) || 'other';
      if (!volGroups[kw]) volGroups[kw] = [];
      volGroups[kw].push(vt);
    });
    Object.keys(volGroups).forEach(kw => {
      let rIdx = addonCfg.variant_rules.findIndex(r => (r.match_tags || (r.match ? [r.match] : [])).includes(kw));
      if (rIdx === -1) { addonCfg.variant_rules.push({match: kw, match_tags: [kw], groups: []}); rIdx = addonCfg.variant_rules.length - 1; }
      delete addonCfg.variant_rules[rIdx]._auto;
      const copy = JSON.parse(JSON.stringify(newGrp));
      copy.id = id + '_' + kw.toLowerCase();
      copy.label = label + ' (' + kw + ')';
      addonCfg.variant_rules[rIdx].groups.push(copy);
      selGrp = {section: rIdx, gIdx: addonCfg.variant_rules[rIdx].groups.length - 1};
    });
  }
  markDirty();
  closeModal('agm-overlay');
  renderCenter();
  renderOpts();
  showToast('Group added — add options in the right panel', 'success');
}

// ── OPTIONS PANEL ──
function renderOptsEmpty() {
  document.getElementById('opts-title').textContent = 'Options';
  document.getElementById('opts-body').innerHTML = '<div class="empty-c" style="height:auto;padding:36px 16px;"><div class="empty-ico" style="font-size:28px;">✦</div><p style="font-size:12px;">Select a group to edit</p></div>';
}

function getGrp(sec, gi) {
  return sec === 'common' ? addonCfg.common[gi] : addonCfg.variant_rules[sec]?.groups?.[gi];
}

function selectGrp(sec, gi) {
  selGrp = {section: sec, gIdx: gi};
  renderCenter();
  renderOpts();
}

function renderOpts() {
  const g = getGrp(selGrp.section, selGrp.gIdx);
  if (!g) return renderOptsEmpty();
  document.getElementById('opts-title').textContent = g.label || 'Options';
  const {section, gIdx} = selGrp;
  const sStr = typeof section === 'number' ? section : `'${section}'`;
  const isImg = g.display === 'image';

  const optsHtml = (g.options || []).map((o, oi) => optRowHTML(sStr, gIdx, oi, o, isImg)).join('');

  document.getElementById('opts-body').innerHTML = `
    <div class="fi">
      <div class="fl">Group Label</div>
      <input class="finput" value="${eh(g.label || '')}" oninput="updG(${sStr},${gIdx},'label',this.value)" placeholder="e.g. Choose Controller">
    </div>
    <div class="fi">
      <div class="fl">Group ID</div>
      <input class="finput" value="${eh(g.id || '')}" oninput="updG(${sStr},${gIdx},'id',this.value)" placeholder="e.g. controller">
    </div>
    <div class="fi">
      <div class="fl">Display Style</div>
      <select class="finput" onchange="updG(${sStr},${gIdx},'display',this.value);renderOpts();">
        <option value="button" ${(g.display || 'button') === 'button' ? 'selected' : ''}>Buttons</option>
        <option value="image" ${g.display === 'image' ? 'selected' : ''}>Image Tiles</option>
      </select>
    </div>
    <div class="divider"></div>
    <div class="fl" style="margin-bottom:7px;">
      Options <span style="font-weight:400;text-transform:none;color:var(--muted);font-size:10px;">${(g.options || []).length} total</span>
    </div>
    ${isImg ? '<div class="img-hint">🖼 Image Tile mode — each option can have an image URL.</div>' : ''}
    <div class="opt-hdr"><div></div><div class="opt-col">Label</div><div class="opt-col">Handle / Variant ID</div><div></div><div></div></div>
    <div id="opts-list">${optsHtml}</div>
    <div style="display:flex;gap:6px;margin-top:8px;">
      <button class="btn btn-ghost btn-full btn-sm" onclick="addOpt(${sStr},${gIdx})">+ Add manually</button>
      <button class="btn btn-primary btn-full btn-sm" onclick="openPicker(${sStr},${gIdx},null)">🔍 Pick product</button>
    </div>
    <div class="fi" style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border);margin-bottom:4px;">
      <div>
        <div class="fl" style="margin-bottom:2px;">Required group</div>
        <div style="font-size:10px;color:var(--muted);">Customer must select an option before adding to cart</div>
      </div>
      <button class="toggle-pill${''}" id="req-toggle-${sStr}-${gIdx}" onclick="toggleRequired(${sStr},${gIdx})"
        style="width:36px;height:20px;border-radius:20px;border:none;cursor:pointer;background:${g.required ? 'var(--success)' : 'var(--border)'};position:relative;transition:background .2s;flex-shrink:0;">
        <span style="position:absolute;width:14px;height:14px;border-radius:50%;background:#fff;top:3px;left:${g.required ? '19px' : '3px'};transition:left .2s;box-shadow:0 1px 3px rgba(0,0,0,.3);"></span>
      </button>
    </div>
    <div class="fi" style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border);margin-bottom:4px;">
      <div>
        <div class="fl" style="margin-bottom:2px;">Allow multiple</div>
        <div style="font-size:10px;color:var(--muted);">Customer can pick more than one option at once (e.g. both end caps)</div>
      </div>
      <button class="toggle-pill" id="ms-toggle-${sStr}-${gIdx}" onclick="toggleMultiSelect(${sStr},${gIdx})"
        style="width:36px;height:20px;border-radius:20px;border:none;cursor:pointer;background:${g.multiSelect ? 'var(--success)' : 'var(--border)'};position:relative;transition:background .2s;flex-shrink:0;">
        <span style="position:absolute;width:14px;height:14px;border-radius:50%;background:#fff;top:3px;left:${g.multiSelect ? '19px' : '3px'};transition:left .2s;box-shadow:0 1px 3px rgba(0,0,0,.3);"></span>
      </button>
    </div>
    <div class="fi" style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border);margin-bottom:4px;">
      <div>
        <div class="fl" style="margin-bottom:2px;">Replaces main product</div>
        <div style="font-size:10px;color:var(--muted);">Selecting from this group skips the base product in cart (e.g. length selectors)</div>
      </div>
      <button class="toggle-pill" id="rm-toggle-${sStr}-${gIdx}" onclick="toggleReplacesMain(${sStr},${gIdx})"
        style="width:36px;height:20px;border-radius:20px;border:none;cursor:pointer;background:${g.replaces_main ? 'var(--success)' : 'var(--border)'};position:relative;transition:background .2s;flex-shrink:0;">
        <span style="position:absolute;width:14px;height:14px;border-radius:50%;background:#fff;top:3px;left:${g.replaces_main ? '19px' : '3px'};transition:left .2s;box-shadow:0 1px 3px rgba(0,0,0,.3);"></span>
      </button>
    </div>
    <div class="divider"></div>
    <div class="info-box">
      <strong>Handle</strong> — single-variant addon, e.g. <code>cct-strip-touch-controller</code><br>
      <strong>Variant ID</strong> — multi-variant addon, e.g. <code>42523404271864</code><br>
      Use <strong>🔍 Pick product</strong> to search all products and auto-fill.
    </div>`;
}

function optRowHTML(sStr, gIdx, oIdx, opt, isImg) {
  const imgSrc = opt.image || '';
  const tid = 'th_' + String(sStr).replace(/'/g, '') + '_' + gIdx + '_' + oIdx;
  const hv = opt.variant_id || opt.handle || '';
  const isBest = opt.best_seller === true;
  const isRec  = opt.recommended === true;

  const pp = hv && !(/^\d+$/.test(hv)) ? ALL_PRODUCTS.find(p => p.h === hv) : null;
  const chipHtml = pp ? `<div class="picked-chip">
    <div class="picked-chip-img">${pp.i ? `<img src="${eh(pp.i)}" onerror="this.style.display='none'">` : '🔌'}</div>
    <div class="picked-chip-info">
      <div class="picked-chip-name">${eh(pp.t)}</div>
      <div class="picked-chip-handle">${eh(hv)}</div>
    </div>
    <button class="pick-btn" onclick="openPicker(${sStr},${gIdx},${oIdx})">Change</button>
  </div>` : '';

  return `<div class="opt-row"
       draggable="true"
       data-oi="${oIdx}"
       ondragstart="onOptDragStart(event,${sStr},${gIdx},${oIdx})"
       ondragover="onOptDragOver(event,${sStr},${gIdx},${oIdx})"
       ondrop="onOptDrop(event,${sStr},${gIdx},${oIdx})"
       ondragend="onOptDragEnd(event)">
    <div class="opt-main">
      <span class="drag-handle" title="Drag to reorder">≡</span>
      <input type="text" value="${eh(opt.label || '')}" placeholder="Label" oninput="updO(${sStr},${gIdx},${oIdx},'label',this.value)">
      <input type="text" value="${eh(hv)}" placeholder="handle or variant ID" oninput="setHV(${sStr},${gIdx},${oIdx},this.value)">
      <button class="star-btn${isBest ? ' on' : ''}" title="${isBest ? 'Best seller — click to unmark' : 'Mark as best seller'}" onclick="toggleBestSeller(${sStr},${gIdx},${oIdx})">${isBest ? '★' : '☆'}</button>
      <button class="rec-btn${isRec ? ' on' : ''}" title="${isRec ? 'Recommended — click to unmark' : 'Mark as Recommended'}" onclick="toggleRecommended(${sStr},${gIdx},${oIdx})">${isRec ? '⚑' : '⚐'}</button>
      <button class="del-x" onclick="delO(${sStr},${gIdx},${oIdx})">
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
    </div>
    ${chipHtml}
    <div class="opt-img-row${isImg ? '' : ' hidden'}">
      <div class="thumb" id="${tid}">${imgSrc ? `<img src="${eh(imgSrc)}" onerror="this.style.display='none'">` : '📷'}</div>
      <input type="text" value="${eh(imgSrc)}" placeholder="https://chronoslights.com/cdn/shop/files/…" oninput="updImg(${sStr},${gIdx},${oIdx},this.value,'${tid}')">
    </div>
  </div>`;
}

// ── PRODUCT PICKER ──
function openPicker(sec, gi, oi) {
  pickerTargetSec = sec; pickerTargetGi = gi; pickerTargetOi = oi;
  pickerSelProd = null; pickerSelVars = [];
  document.getElementById('picker-confirm-btn').disabled = true;
  document.getElementById('picker-varpick').style.display = 'none';
  document.getElementById('picker-search-input').value = '';
  pickerFiltered = [...ALL_PRODUCTS];
  renderPickerList(pickerFiltered);
  document.getElementById('picker-overlay').classList.add('show');
  setTimeout(() => document.getElementById('picker-search-input').focus(), 120);
}

function filterPicker(q) {
  q = q.toLowerCase().trim();
  pickerFiltered = q ? ALL_PRODUCTS.filter(p => p.h.includes(q) || p.t.toLowerCase().includes(q)) : [...ALL_PRODUCTS];
  pickerSelProd = null; pickerSelVars = [];
  document.getElementById('picker-varpick').style.display = 'none';
  document.getElementById('picker-confirm-btn').disabled = true;
  renderPickerList(pickerFiltered);
}

function renderPickerList(list) {
  const el = document.getElementById('picker-body');
  // Same infrastructure filter as renderProdList — the storage product must
  // never be pickable as an addon.
  list = list.filter(p => String(p.gid || '') !== '10236050931960');
  if (!list.length) { el.innerHTML = '<div style="color:var(--muted);font-size:11px;padding:14px;text-align:center;">No results</div>'; return; }
  el.innerHTML = list.slice(0, 100).map(p => {
    const isSel = pickerSelProd && pickerSelProd.h === p.h;
    const imgHtml = p.i
      ? `<img class="picker-img" src="${eh(p.i)}" onerror="this.style.display='none'">`
      : `<div class="picker-img" style="display:flex;align-items:center;justify-content:center;font-size:13px;">🔌</div>`;
    const vc = p.v ? p.v.length : 0;
    return `<div class="picker-row${isSel ? ' sel' : ''}" onclick="pickProduct('${eh(p.h)}')">
      ${imgHtml}
      <div class="picker-info">
        <div class="picker-name">${eh(p.t)}</div>
        <div class="picker-handle">${eh(p.h)}</div>
        <div class="picker-vmeta">${vc > 1 ? vc + ' variants — select one below' : 'Single variant'}</div>
      </div>
    </div>`;
  }).join('');

  // The list is capped at 100 for rendering speed — say so, otherwise a search
  // that matches 300 products silently looks like it matched 100.
  if (list.length > 100) {
    el.innerHTML += '<div style="color:var(--muted);font-size:11px;padding:10px 14px;text-align:center;border-top:1px solid var(--border);margin-top:6px;">'
      + 'Showing first 100 of ' + list.length + ' — type in the search box to narrow it down.</div>';
  }
}

async function pickProduct(handle) {
  pickerSelProd = ALL_PRODUCTS.find(p => p.h === handle);
  pickerSelVars = [];
  renderPickerList(pickerFiltered);

  const varpickWrap = document.getElementById('picker-varpick');
  const confirmBtn  = document.getElementById('picker-confirm-btn');
  const grid        = document.getElementById('varpick-grid');

  if (!pickerSelProd.v || pickerSelProd.v.length <= 1) {
    varpickWrap.style.display = 'none';
    pickerSelVars = [{id: pickerSelProd.h, title: pickerSelProd.t.substring(0,40), price: ''}];
    confirmBtn.disabled = false;
    confirmBtn.textContent = 'Add 1 Option';
    return;
  }

  varpickWrap.style.display = '';
  confirmBtn.disabled = true;
  grid.innerHTML = '<div style="color:var(--muted);font-size:11px;padding:6px;display:flex;align-items:center;gap:6px;"><div class="spinner"></div>Loading…</div>';

  let vars = [];
  if (pickerCache[handle]) {
    vars = pickerCache[handle].variants;
  } else {
    try {
      const res  = await fetch('https://chronoslights.com/products/' + handle + '.json');
      const data = await res.json();
      vars = data.product.variants.map(v => ({id: String(v.id), title: v.title, price: v.price, sku: v.sku || ''}));
      const imgs = (data.product.images || []).map(i => ({id: String(i.id), src: i.src}));
      pickerCache[handle] = {variants: vars, images: imgs};
    } catch(e) {
      vars = (pickerSelProd.v || []).map((v, i) => ({id: String(i), title: v, price: '', sku: ''}));
    }
  }

  grid.innerHTML = vars.map(v => `
    <div class="varpick-item" onclick="toggleVariant('${eh(v.id)}','${eh(v.title)}','${eh(v.price || '')}',this)">
      <span style="display:flex;align-items:center;gap:7px;">
        <span class="vpick-chk" id="vpchk-${eh(v.id)}" style="width:14px;height:14px;border-radius:3px;border:1.5px solid var(--border);flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:9px;"></span>
        <span>${eh(v.title)}</span>
      </span>
      <span class="varpick-price">${v.price ? 'Rs.' + v.price : ''}</span>
    </div>`).join('');
  updatePickerBtn();
}

function toggleVariant(id, title, price, el) {
  const idx = pickerSelVars.findIndex(v => v.id === id);
  if (idx >= 0) {
    pickerSelVars.splice(idx, 1);
    el.classList.remove('sel');
    const chk = document.getElementById('vpchk-' + id);
    if (chk) { chk.textContent = ''; chk.style.background = ''; chk.style.borderColor = 'var(--border)'; }
  } else {
    pickerSelVars.push({id, title, price});
    el.classList.add('sel');
    const chk = document.getElementById('vpchk-' + id);
    if (chk) { chk.textContent = '✓'; chk.style.background = 'var(--accent)'; chk.style.color = '#0f0f11'; chk.style.borderColor = 'var(--accent)'; }
  }
  updatePickerBtn();
}

function updatePickerBtn() {
  const btn = document.getElementById('picker-confirm-btn');
  if (!btn) return;
  const n = pickerSelVars.length;
  if (n === 0) {
    btn.disabled = true;
    btn.textContent = 'Add as Option';
  } else {
    btn.disabled = false;
    btn.textContent = n === 1 ? 'Add 1 Option' : 'Add ' + n + ' Options';
  }
}

function confirmPick() {
  if (!pickerSelProd) return;
  let imgUrl = '';
  const cache = pickerCache[pickerSelProd.h];
  if (cache && cache.images && cache.images.length) imgUrl = cache.images[0].src;
  else if (pickerSelProd.i) imgUrl = pickerSelProd.i;

  const g = getGrp(pickerTargetSec, pickerTargetGi);
  if (!g) { closeModal('picker-overlay'); return; }

  const isImplicitSingle = pickerSelVars.length === 1 && pickerSelVars[0].id === pickerSelProd.h;
  const toAdd = pickerSelVars.length > 0 ? pickerSelVars : [{id: pickerSelProd.h, title: pickerSelProd.t.substring(0,40), price: ''}];

  if (pickerTargetOi !== null && toAdd.length === 1) {
    const v = toAdd[0];
    const isVarId = !isImplicitSingle;
    if (g.options[pickerTargetOi] != null) {
      const o = g.options[pickerTargetOi];
      o.label = v.title;
      if (isVarId) { o.variant_id = v.id; delete o.handle; }
      else         { o.handle = v.id;     delete o.variant_id; }
      if (imgUrl) o.image = imgUrl;
      markDirty();
    }
  } else {
    toAdd.forEach(v => {
      const isVarId = !isImplicitSingle;
      const newOpt = {label: v.title};
      if (isVarId) newOpt.variant_id = v.id;
      else         newOpt.handle     = v.id;
      if (imgUrl) newOpt.image = imgUrl;
      g.options.push(newOpt);
    });
    markDirty();
  }
  closeModal('picker-overlay');
  renderOpts();
  showToast(toAdd.length + ' option' + (toAdd.length > 1 ? 's' : '') + ' added', 'success');
}

// ── MUTATIONS ──
function updG(sec, gi, k, v) {
  const g = getGrp(sec, gi);
  if (g) { g[k] = v; markDirty(); }
  if (k === 'label') document.getElementById('opts-title').textContent = v || 'Options';
}
function updSummaryLabel(key, val) {
  if (!addonCfg.summary_labels) addonCfg.summary_labels = {};
  addonCfg.summary_labels[key] = val;
  markDirty();
}
function updO(sec, gi, oi, k, v) {
  const g = getGrp(sec, gi);
  if (g && g.options[oi] != null) { g.options[oi][k] = v; markDirty(); }
}
function setHV(sec, gi, oi, v) {
  const g = getGrp(sec, gi);
  if (!g || g.options[oi] == null) return;
  const o = g.options[oi];
  if (/^\d+$/.test(v.trim())) { o.variant_id = v.trim(); delete o.handle; }
  else { o.handle = v.trim(); delete o.variant_id; }
  markDirty();
  renderOpts();
}
function updImg(sec, gi, oi, v, tid) {
  const g = getGrp(sec, gi);
  if (!g || g.options[oi] == null) return;
  if (v.trim()) g.options[oi].image = v.trim(); else delete g.options[oi].image;
  markDirty();
  const th = document.getElementById(tid);
  if (th) th.innerHTML = v.trim() ? `<img src="${eh(v.trim())}" onerror="this.style.display='none'">` : '📷';
}
function addOpt(sec, gi) {
  const g = getGrp(sec, gi);
  if (!g) return;
  g.options.push({label: '', handle: ''});
  markDirty(); renderOpts();
}
function delO(sec, gi, oi) {
  const g = getGrp(sec, gi);
  if (!g) return;
  g.options.splice(oi, 1);
  markDirty(); renderOpts();
}
function deleteGrp(sec, gi) {
  if (!confirm('Delete this group?')) return;
  if (sec === 'common') addonCfg.common.splice(gi, 1);
  else addonCfg.variant_rules[sec].groups.splice(gi, 1);
  selGrp = null; markDirty(); renderCenter(); renderOptsEmpty();
}
function deleteRule(rIdx) {
  if (!confirm('Delete rule and all groups?')) return;
  addonCfg.variant_rules.splice(rIdx, 1);
  if (selGrp && selGrp.section === rIdx) { selGrp = null; renderOptsEmpty(); }
  markDirty(); renderCenter();
}
function editRuleKw(rIdx) {
  if (fetchedVars && fetchedVars.length) {
    openRulePicker(rIdx);
  } else {
    const rule = addonCfg.variant_rules[rIdx];
    const cur  = (rule.match_tags || (rule.match ? [rule.match] : [])).join(', ');
    const v    = prompt('Match keywords (comma separated):', cur);
    if (v === null) return;
    const tags = v.split(',').map(t => t.trim()).filter(Boolean);
    rule.match_tags = tags; rule.match = tags[0] || '';
    delete rule._auto;
    markDirty(); renderCenter();
  }
}
function addRuleManual() {
  if (!fetchedVars || !fetchedVars.length) {
    const kw = prompt('Match keyword (e.g. 12V, 24V, Red):','');
    if (!kw || !kw.trim()) return;
    addonCfg.variant_rules.push({match: kw.trim(), match_tags: [kw.trim()], groups: []});
    markDirty(); renderCenter();
    return;
  }
  openRulePicker(-1);
}

// ── RULE PICKER ──
let rulePicking = -1;

function openRulePicker(rIdx) {
  rulePicking = rIdx;
  const rule = rIdx >= 0 ? addonCfg.variant_rules[rIdx] : null;
  const curTags = rule ? (rule.match_tags || (rule.match ? [rule.match] : [])) : [];

  if (!document.getElementById('rulepick-overlay')) { buildRulePickerModal(); }

  const confirmBtn = document.getElementById('rulepick-confirm-btn');
  if (confirmBtn) confirmBtn.textContent = rIdx === -1 ? 'Create Rule' : 'Update Rule';

  const grid = document.getElementById('rulepick-grid');
  grid.innerHTML = fetchedVars.map(v => {
    const isSel = curTags.some(t => v.title.toLowerCase().includes(t.toLowerCase()));
    return `<div class="varpick-item${isSel ? ' sel' : ''}" onclick="toggleRuleVar(this,'${eh(v.title.replace(/'/g,"\'"))}')">
      <span style="display:flex;align-items:center;gap:7px;">
        <span class="rpchk" style="width:14px;height:14px;border-radius:3px;border:1.5px solid var(--border);flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:9px;${isSel ? 'background:var(--accent);color:#0f0f11;border-color:var(--accent);' : ''}">${isSel ? '✓' : ''}</span>
        <span>${eh(v.title)}</span>
      </span>
    </div>`;
  }).join('');

  document.getElementById('rulepick-kw').value = curTags.join(', ');
  document.getElementById('rulepick-overlay').classList.add('show');
}

function buildRulePickerModal() {
  const el = document.createElement('div');
  el.id = 'rulepick-overlay';
  el.className = 'modal-overlay';
  el.innerHTML = [
    '<div class="modal" style="width:500px;max-height:85vh;display:flex;flex-direction:column;">',
    '<h3 style="margin-bottom:4px;">Variant Rule — Which variants trigger this?</h3>',
    '<p class="modal-sub">Tick the variants that should show this group. Keywords auto-fill below.</p>',
    '<div style="flex:1;overflow-y:auto;"><div class="varpick-grid" id="rulepick-grid" style="max-height:300px;padding:4px 0;"></div></div>',
    '<div style="margin-top:10px;padding-top:10px;border-top:1px solid var(--border);">',
    '<div style="font-size:10px;font-weight:600;text-transform:uppercase;color:var(--muted);margin-bottom:4px;">Or type custom keywords (comma separated)</div>',
    '<input type="text" id="rulepick-kw" class="finput" placeholder="e.g. 12V, Warm White, Red">',
    '<div style="font-size:10px;color:var(--muted);margin-top:3px;">Ticking variants above auto-fills this field.</div>',
    '</div>',
    '<div class="modal-actions" style="margin-top:10px;">',
    '<button class="btn btn-ghost" onclick="closeModal(\'rulepick-overlay\')">Cancel</button>',
    '<button class="btn btn-primary" id="rulepick-confirm-btn" onclick="confirmRulePick()">Create Rule</button>',
    '</div></div>'
  ].join('');
  document.body.appendChild(el);
  el.addEventListener('click', e => { if (e.target === el) el.classList.remove('show'); });
}

function toggleRuleVar(el, title) {
  el.classList.toggle('sel');
  const chk = el.querySelector('.rpchk');
  const isSel = el.classList.contains('sel');
  if (chk) {
    chk.textContent = isSel ? '✓' : '';
    chk.style.background = isSel ? 'var(--accent)' : '';
    chk.style.color = isSel ? '#0f0f11' : '';
    chk.style.borderColor = isSel ? 'var(--accent)' : 'var(--border)';
  }
  const selTitles = Array.from(document.querySelectorAll('#rulepick-grid .varpick-item.sel'))
    .map(e => e.querySelector('span > span:last-child').textContent.trim());
  const kwField = document.getElementById('rulepick-kw');
  if (selTitles.length > 0) {
    kwField.value = selTitles.join(', ');
  } else {
    kwField.value = '';
  }
}

function confirmRulePick() {
  const kw = document.getElementById('rulepick-kw').value.trim();
  if (!kw) { showToast('Select at least one variant or enter a keyword', 'error'); return; }
  const tags = kw.split(',').map(t => t.trim()).filter(Boolean);

  if (rulePicking === -1) {
    addonCfg.variant_rules.push({match: tags[0], match_tags: tags, groups: []});
  } else {
    addonCfg.variant_rules[rulePicking].match = tags[0];
    addonCfg.variant_rules[rulePicking].match_tags = tags;
    delete addonCfg.variant_rules[rulePicking]._auto;
  }
  markDirty();
  closeModal('rulepick-overlay');
  renderCenter();
}

function markDirty() { dirty = true; document.getElementById('btn-save').disabled = false; }

// ── GROUP DRAG-REORDER ──
let _grpDrag = null; // {section, gi}

function _grpArrayFor(section) {
  return section === 'common' ? addonCfg.common : addonCfg.variant_rules[section]?.groups;
}

function onGrpDragStart(e, section, gi) {
  _grpDrag = {section, gi};
  e.dataTransfer.effectAllowed = 'move';
  // Required for Firefox
  try { e.dataTransfer.setData('text/plain', String(gi)); } catch(err) {}
  e.currentTarget.classList.add('dragging');
}

function onGrpDragOver(e, section, gi) {
  if (!_grpDrag || _grpDrag.section !== section) return; // no cross-section drag
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
}

function onGrpDrop(e, section, gi) {
  if (!_grpDrag || _grpDrag.section !== section) return; // no cross-section drag
  e.preventDefault();
  const arr = _grpArrayFor(section);
  if (!arr) return;
  const from = _grpDrag.gi;
  const to   = gi;
  if (from === to) return;
  const [moved] = arr.splice(from, 1);
  arr.splice(to, 0, moved);

  // Keep selGrp pointing at the same group object after reorder
  if (selGrp && selGrp.section === section) {
    if (selGrp.gIdx === from) selGrp.gIdx = to;
    else if (from < to && selGrp.gIdx > from && selGrp.gIdx <= to) selGrp.gIdx--;
    else if (from > to && selGrp.gIdx >= to && selGrp.gIdx < from) selGrp.gIdx++;
  }

  markDirty();
  renderCenter();
}

function onGrpDragEnd(e) {
  e.currentTarget.classList.remove('dragging');
  _grpDrag = null;
}


// ── BUILD + SAVE ──
function buildCfg() {
  // Only emit summary_labels when at least one is set — an all-blank object
  // in every product's metafield is noise, and the storefront already falls
  // back to the markup defaults when the key is absent.
  const sl = addonCfg.summary_labels || {};
  const slOut = {};
  ['base', 'addons', 'total'].forEach(k => {
    if (sl[k] && String(sl[k]).trim()) slOut[k] = String(sl[k]).trim();
  });

  return {
    ...(Object.keys(slOut).length ? { summary_labels: slOut } : {}),
    common: addonCfg.common.map(cg),
    variant_rules: addonCfg.variant_rules
      // ── Remove auto-detected rules that still have no groups ──
      .filter(function(rule) {
        const isAuto = rule._auto === true;
        const hasGroups = (rule.groups || []).length > 0;
        return !isAuto || hasGroups;
      })
      .map(function(rule) {
        const tags = rule.match_tags || (rule.match ? [rule.match] : []);
        const out = {
          match: tags[0] || '',
          match_tags: tags,   // always include both for consistency
          groups: (rule.groups || []).map(cg)
        };
        return out;
      })
  };
}

function doSave() {
  if (!selectedProd) { showToast('Select a product first', 'error'); return; }
  const cfg  = buildCfg();
  localStorage.setItem('ca6_' + selectedProd.h, JSON.stringify(cfg));
  const json = JSON.stringify(cfg, null, 2);
  navigator.clipboard.writeText(json)
    .then(() => showToast('Copied ✓ — paste into Shopify metafield', 'success'))
    .catch(() => { previewJSON(); showToast('Copy from modal', 'info'); });
  dirty = false;
  renderProdList(filteredProds);
  updateConfiguredLabel();
}

// ── IMPORT ──
function openImport() {
  if (!selectedProd) { showToast('Select a product first', 'error'); return; }
  const sel = document.getElementById('import-src');
  sel.innerHTML = '<option value="">— pick a product —</option>';
  ALL_PRODUCTS.forEach(p => {
    if (p.h === selectedProd.h || !localStorage.getItem('ca6_' + p.h)) return;
    sel.innerHTML += `<option value="${eh(p.h)}">${eh(p.t.substring(0, 55))}</option>`;
  });
  document.getElementById('import-json').value = '';
  switchTab('paste');
  document.getElementById('import-overlay').classList.add('show');
}
function switchTab(t) {
  document.getElementById('tab-paste').classList.toggle('active', t === 'paste');
  document.getElementById('tab-copy').classList.toggle('active',  t === 'copy');
  document.getElementById('pane-paste').style.display = t === 'paste' ? '' : 'none';
  document.getElementById('pane-copy').style.display  = t === 'copy'  ? '' : 'none';
}
function doImport() {
  const isPaste = document.getElementById('tab-paste').classList.contains('active');
  let p;
  if (isPaste) {
    const r = document.getElementById('import-json').value.trim();
    if (!r) { showToast('Paste JSON first', 'error'); return; }
    try { p = JSON.parse(r); } catch(e) { showToast('Invalid JSON', 'error'); return; }
  } else {
    const s = document.getElementById('import-src').value;
    if (!s) { showToast('Select a product', 'error'); return; }
    try { p = JSON.parse(localStorage.getItem('ca6_' + s)); } catch(e) { showToast('Corrupt data', 'error'); return; }
  }
  if (!p || !('common' in p)) { showToast('Must have "common" key', 'error'); return; }
  addonCfg = {common: p.common || [], variant_rules: p.variant_rules || [], summary_labels: p.summary_labels || {}};
  selGrp = null; markDirty(); renderCenter(); renderOptsEmpty();
  closeModal('import-overlay');
  showToast('Config imported ✓', 'success');
}

// ── JSON + TREE ──
function previewJSON() {
  document.getElementById('json-pre').textContent = JSON.stringify(buildCfg(), null, 2);
  document.getElementById('json-overlay').classList.add('show');
}
function copyJSON() {
  navigator.clipboard.writeText(JSON.stringify(buildCfg(), null, 2)).then(() => showToast('Copied!', 'success'));
}
function openTree() {
  if (!selectedProd) { showToast('Select a product first', 'error'); return; }
  const cfg = buildCfg();
  const lines = [];
  lines.push(`<span class="tp">📦 ${eh(selectedProd.t)}</span>`);
  if (cfg.common.length) {
    lines.push(`<span class="tc">   ├── COMMON</span>`);
    cfg.common.forEach((g, gi) => {
      const last = gi === cfg.common.length - 1 && !cfg.variant_rules.length;
      lines.push(`<span class="tg">   ${last ? '└' : '├'}── [${g.display}] ${eh(g.label)}</span>`);
      (g.options || []).forEach((o, oi) => {
        lines.push(`<span class="to">   ${last ? ' ' : '│'}   ${oi === g.options.length - 1 ? '└' : '├'}── ${eh(o.label)}${o.image ? ' 🖼' : ''}</span>`);
      });
    });
  }
  cfg.variant_rules.forEach((r, ri) => {
    const tags = r.match_tags || (r.match ? [r.match] : []);
    const lr = ri === cfg.variant_rules.length - 1;
    lines.push(`<span class="tr">   ${lr ? '└' : '├'}── RULE: "${eh(tags.join('/'))}"</span>`);
    (r.groups || []).forEach((g, gi) => {
      const lg = gi === r.groups.length - 1;
      lines.push(`<span class="tg">   ${lr ? ' ' : '│'}   ${lg ? '└' : '├'}── [${g.display}] ${eh(g.label)}</span>`);
      (g.options || []).forEach((o, oi) => {
        lines.push(`<span class="to">   ${lr ? ' ' : '│'}   ${lg ? ' ' : '│'}   ${oi === g.options.length - 1 ? '└' : '├'}── ${eh(o.label)}${o.image ? ' 🖼' : ''}</span>`);
      });
    });
  });
  if (!cfg.common.length && !cfg.variant_rules.length) lines.push('<span style="color:var(--muted);">   (empty)</span>');
  document.getElementById('tree-body').innerHTML = lines.join('\n');
  document.getElementById('tree-overlay').classList.add('show');
}


function loadConfiguredCache() {
  // Local pass: any product with a saved ca6_<handle> entry on THIS browser —
  // matches the same simple existence check used for the "Config ✓" tag on
  // product cards and the Import modal's "Copy from Product" list.
  const map = new Map();
  ALL_PRODUCTS.forEach(p => {
    const raw = localStorage.getItem('ca6_' + p.h);
    if (raw) map.set(p.h, { h: p.h, t: p.t, local: true, shopify: false });
  });

  // Shopify pass: last results from "Check Shopify" (cross-device, only as fresh as the last check).
  let shopify = [];
  try { shopify = JSON.parse(localStorage.getItem('ca_shopify_configured') || '[]'); }
  catch(e) { shopify = []; }
  shopify.forEach(p => {
    const existing = map.get(p.h);
    if (existing) existing.shopify = true;
    else map.set(p.h, { h: p.h, t: p.t, local: false, shopify: true });
  });

  configuredProducts = Array.from(map.values()).sort((a, b) => (a.t || a.h).localeCompare(b.t || b.h));
  configuredFiltered = [...configuredProducts];
}

function updateConfiguredLabel() {
  const el = document.getElementById('cfg-count');
  if (el) el.textContent = configuredProducts.length ? '(' + configuredProducts.length + ')' : '';
}

function updateConfiguredSyncLabel() {
  const el = document.getElementById('configured-sync-label');
  if (!el) return;
  const at = localStorage.getItem('ca_shopify_configured_at');
  el.textContent = at
    ? 'Configs saved on this device, plus Shopify-verified as of ' + new Date(at).toLocaleString()
    : 'Showing configs saved on this device — click "Check Shopify" to also verify across devices';
}

function openConfiguredList() {
  loadConfiguredCache();
  document.getElementById('configured-search-input').value = '';
  renderConfiguredList(configuredFiltered);
  updateConfiguredSyncLabel();
  document.getElementById('configured-overlay').classList.add('show');
  setTimeout(() => document.getElementById('configured-search-input').focus(), 120);
}

function filterConfiguredList(q) {
  q = q.toLowerCase().trim();
  configuredFiltered = q
    ? configuredProducts.filter(p => p.h.includes(q) || (p.t || '').toLowerCase().includes(q))
    : [...configuredProducts];
  renderConfiguredList(configuredFiltered);
}

function renderConfiguredList(list) {
  const el  = document.getElementById('configured-body');
  const cnt = document.getElementById('configured-foot-count');
  if (cnt) cnt.textContent = list.length + ' configured product' + (list.length === 1 ? '' : 's');
  if (!list.length) {
    el.innerHTML = '<div style="color:var(--muted);font-size:11px;padding:14px;text-align:center;">No configured products yet — save or push a config to see it here.</div>';
    return;
  }
  el.innerHTML = list.map(p => {
    const local = ALL_PRODUCTS.find(x => x.h === p.h);
    const img = local && local.i ? local.i : '';
    const imgHtml = img
      ? `<img class="picker-img" src="${eh(img)}" onerror="this.style.display='none'">`
      : `<div class="picker-img" style="display:flex;align-items:center;justify-content:center;font-size:13px;">🔌</div>`;
    const tags = [];
    if (p.local)   tags.push('<span class="ptag cfg">Config ✓</span>');
    if (p.shopify) tags.push('<span class="ptag volt">Shopify ✓</span>');
    return `<div class="picker-row" onclick="openConfiguredProduct('${eh(p.h)}')">
      ${imgHtml}
      <div class="picker-info">
        <div class="picker-name">${eh(p.t || p.h)}</div>
        <div class="picker-handle">${eh(p.h)}</div>
        ${tags.length ? `<div class="pcard-tags" style="margin-top:3px;">${tags.join('')}</div>` : ''}
      </div>
    </div>`;
  }).join('');
}

function openConfiguredProduct(handle) {
  closeModal('configured-overlay');
  const p = ALL_PRODUCTS.find(x => x.h === handle);
  if (p) {
    selectProd(handle);
  } else {
    document.getElementById('handle-search-input').value = handle;
    loadByHandle();
  }
}

function hasRealAddonConfig(jsonStr) {
  if (!jsonStr) return false;
  let cfg;
  try { cfg = JSON.parse(jsonStr); } catch(e) { return false; }
  const hasCommon = Array.isArray(cfg.common) && cfg.common.length > 0;
  const hasRules  = Array.isArray(cfg.variant_rules) && cfg.variant_rules.some(r => (r.groups || []).length > 0);
  return hasCommon || hasRules;
}

async function checkConfiguredFromShopify() {
  const token    = localStorage.getItem('ca_shpat') || '';
  const proxyUrl = (localStorage.getItem('ca_proxy_url') || '').replace(/\/$/, '');
  if (!token || !proxyUrl) {
    showToast('Add API token + Proxy URL in Settings first', 'error');
    openProxySettings();
    return;
  }

  const btn = document.getElementById('btn-check-configured');
  if (btn) { btn.disabled = true; btn.textContent = '⟳ Checking…'; }

  const query = `
    query($cursor: String) {
      products(first: 50, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        edges {
          node {
            handle
            title
            metafield(namespace: "custom", key: "addon_groups") { value }
          }
        }
      }
    }`;

  const found = [];
  let cursor  = null;
  let hasNext = true;
  let pages   = 0;

  try {
    while (hasNext) {
      const resp = await shopifyFetch('/admin/api/2024-04/graphql.json', {
        method: 'POST',
        body: JSON.stringify({ query, variables: { cursor } })
      });
      if (!resp.ok) throw new Error('GraphQL HTTP ' + resp.status);
      const data = await resp.json();
      if (data.errors) throw new Error(data.errors.map(e => e.message).join('; '));

      const conn = data.data.products;
      conn.edges.forEach(edge => {
        const n = edge.node;
        if (hasRealAddonConfig(n.metafield && n.metafield.value)) {
          found.push({ h: n.handle, t: n.title });
        }
      });

      hasNext = conn.pageInfo.hasNextPage;
      cursor  = conn.pageInfo.endCursor;
      pages++;
      if (btn) btn.textContent = '⟳ Checking… (' + (pages * 50) + ')';
      if (hasNext) await new Promise(r => setTimeout(r, 200)); // gentle on GraphQL rate limit
    }

    configuredProducts = found;
    configuredFiltered = [...found];
    localStorage.setItem('ca_shopify_configured', JSON.stringify(found));
    localStorage.setItem('ca_shopify_configured_at', new Date().toISOString());
    loadConfiguredCache();
    renderConfiguredList(configuredFiltered);
    updateConfiguredSyncLabel();
    updateConfiguredLabel();
    showToast('Found ' + found.length + ' configured product' + (found.length === 1 ? '' : 's') + ' on Shopify', 'success');
  } catch (e) {
    console.error('[CheckConfigured]', e);
    showToast('Check failed: ' + e.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = '🔄 Check Shopify'; }
  }
}

// ── UTILS ──
function closeModal(id) {
  const el = document.getElementById(id);
  if (el) el.classList.remove('show');
  if (id === 'picker-overlay') {
    const btn = document.getElementById('picker-confirm-btn');
    if (btn) { btn.textContent = 'Add as Option'; btn.disabled = true; }
    pickerSelVars = [];
  }
}
let _tt;
function showToast(msg, type = 'success') {
  const el = document.getElementById('toast');
  el.className = 'toast show ' + type;
  document.getElementById('toast-msg').textContent = msg;
  clearTimeout(_tt);
  _tt = setTimeout(() => el.classList.remove('show'), 3000);
}
function eh(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function toggleRequired(sec, gi) {
  const g = getGrp(sec, gi);
  if (!g) return;
  g.required = !g.required;
  markDirty();
  renderOpts();
}

function toggleReplacesMain(sec, gi) {
  const g = getGrp(sec, gi);
  if (!g) return;
  g.replaces_main = !g.replaces_main;
  markDirty();
  renderOpts();
  renderCenter();
}

function toggleMultiSelect(sec, gi) {
  const g = getGrp(sec, gi);
  if (!g) return;
  g.multiSelect = !g.multiSelect;
  markDirty();
  renderOpts();
}

function toggleBestSeller(sec, gi, oi) {
  const g = getGrp(sec, gi);
  if (!g || g.options[oi] == null) return;
  g.options[oi].best_seller = !g.options[oi].best_seller;
  markDirty();
  renderOpts();
}

function toggleRecommended(sec, gi, oi) {
  const g = getGrp(sec, gi);
  if (!g || g.options[oi] == null) return;
  g.options[oi].recommended = !g.options[oi].recommended;
  markDirty();
  renderOpts();
}

// ── OPTION DRAG-REORDER ──
let _optDrag = null; // {oi}

function onOptDragStart(e, sec, gi, oi) {
  _optDrag = {oi};
  e.dataTransfer.effectAllowed = 'move';
  try { e.dataTransfer.setData('text/plain', String(oi)); } catch(err) {}
  e.currentTarget.classList.add('dragging');
}

function onOptDragOver(e, sec, gi, oi) {
  if (!_optDrag) return;
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
}

function onOptDrop(e, sec, gi, oi) {
  if (!_optDrag) return;
  e.preventDefault();
  const g = getGrp(sec, gi);
  if (!g || !g.options) return;
  const from = _optDrag.oi;
  const to   = oi;
  if (from === to) return;
  const [moved] = g.options.splice(from, 1);
  g.options.splice(to, 0, moved);
  markDirty();
  renderOpts();
}

function onOptDragEnd(e) {
  e.currentTarget.classList.remove('dragging');
  _optDrag = null;
}


// ── THEME TOGGLE ──
function toggleTheme() {
  const isLight = document.documentElement.classList.toggle('light');
  const btn = document.getElementById('theme-toggle-btn');
  if (btn) btn.textContent = isLight ? '🌑 Dark' : '🌓 Light';
  localStorage.setItem('ca_theme', isLight ? 'light' : 'dark');
}
(function() {
  if (localStorage.getItem('ca_theme') === 'light') {
    document.documentElement.classList.add('light');
    document.addEventListener('DOMContentLoaded', function() {
      var b = document.getElementById('theme-toggle-btn');
      if (b) b.textContent = '🌑 Dark';
    });
  }
})();

// ── SETTINGS ──
function openProxySettings() {
  var el = document.getElementById('proxy-overlay');
  if (!el) return;
  document.getElementById('cfg-token').value     = localStorage.getItem('ca_shpat') || '';
  document.getElementById('cfg-proxy-url').value = localStorage.getItem('ca_proxy_url') || '';
  el.classList.add('show');
}

function saveSettings() {
  // Strip accidental whitespace and quotes that break token auth
  var token    = document.getElementById('cfg-token').value.trim().replace(/^["']|["']$/g, '');
  var pass     = document.getElementById('cfg-page-pass').value.trim();
  var proxyUrl = document.getElementById('cfg-proxy-url').value.trim().replace(/\/$/, '').replace(/^["']|["']$/g, '');

  if (token)    localStorage.setItem('ca_shpat', token);
  if (pass)     localStorage.setItem('ca_page_pass', pass);
  if (proxyUrl) localStorage.setItem('ca_proxy_url', proxyUrl);

  // Show what was saved so user can verify
  var savedToken = localStorage.getItem('ca_shpat') || '';
  var savedProxy = localStorage.getItem('ca_proxy_url') || '';
  console.log('[Settings] Token saved, length:', savedToken.length, '| Proxy:', savedProxy);

  document.getElementById('proxy-overlay').classList.remove('show');
  enablePushBtn();
  showToast('Settings saved ✓', 'success');
}

// ── ENABLE PUSH BUTTON ──
function enablePushBtn() {
  var token    = (localStorage.getItem('ca_shpat') || '').trim().replace(/^["']|["']$/g, '');
  var proxyUrl = (localStorage.getItem('ca_proxy_url') || '').trim();
  var enabled  = token.length > 10 && proxyUrl.length > 10 && !!selectedProd;
  ['btn-push','btn-push-panel'].forEach(function(id) {
    var btn = document.getElementById(id);
    if (!btn) return;
    btn.disabled      = !enabled;
    btn.style.opacity = enabled ? '1' : '0.5';
    btn.style.cursor  = enabled ? 'pointer' : 'not-allowed';
    btn.title = !token    ? 'Add API token in Settings'  :
                !proxyUrl ? 'Add Proxy URL in Settings'  :
                !selectedProd ? 'Select a product first' : '';
  });
}

// ── TEST CONNECTION ──
async function testConnection() {
  var token    = document.getElementById('cfg-token').value.trim().replace(/^["']|["']$/g, '');
  var proxyUrl = document.getElementById('cfg-proxy-url').value.trim().replace(/\/$/, '');
  var el       = document.getElementById('cfg-test-result');

  if (!token || !proxyUrl) {
    el.textContent = '⚠ Fill in both token and proxy URL first';
    el.style.color = 'var(--danger)';
    return;
  }

  el.textContent = 'Testing…';
  el.style.color = 'var(--muted)';

  try {
    var resp = await fetch(proxyUrl + '/admin/api/2024-04/shop.json', {
      headers: {
        'X-Shopify-Access-Token': token,
        'Content-Type':           'application/json',
      },
    });
    var data = await resp.json().catch(() => ({}));
    if (resp.ok && data.shop) {
      el.textContent = '✓ Connected to: ' + data.shop.name + ' (' + data.shop.domain + ')';
      el.style.color = 'var(--success)';
    } else if (resp.status === 401) {
      el.textContent = '✗ 401 — Token invalid or missing scopes. Check: read_products, read_metafields, write_metafields';
      el.style.color = 'var(--danger)';
    } else if (resp.status === 403) {
      el.textContent = '✗ 403 — Proxy blocked request. Origin: ' + (data.receivedOrigin || 'unknown');
      el.style.color = 'var(--danger)';
    } else {
      el.textContent = '✗ Error ' + resp.status + ': ' + JSON.stringify(data).substring(0, 120);
      el.style.color = 'var(--danger)';
    }
  } catch (err) {
    el.textContent = '✗ Network error: ' + err.message + ' — Check proxy URL is correct';
    el.style.color = 'var(--danger)';
  }
}

// ── STATUS ──
function setStatus(msg, type) {
  var el = document.getElementById('push-status');
  if (!el) return;
  el.style.color = type === 'success' ? 'var(--success)' : type === 'error' ? 'var(--danger)' : 'var(--muted)';
  el.textContent = msg;
}

// ── PUSH TO SHOPIFY ──
async function shopifyFetch(path, opts) {
  // Strip whitespace and any accidental surrounding quotes from stored values
  var token    = (localStorage.getItem('ca_shpat') || '').trim().replace(/^["']|["']$/g, '');
  var proxyUrl = (localStorage.getItem('ca_proxy_url') || '').trim().replace(/\/$/, '').replace(/^["']|["']$/g, '');

  if (!proxyUrl) {
    throw new Error('No proxy URL set — open Settings and paste your Cloudflare Worker URL');
  }
  if (!token) {
    throw new Error('No API token set — open Settings and paste your shpat_xxx token');
  }

  var url = proxyUrl + path;
  console.log('[shopifyFetch]', opts.method || 'GET', url, 'token length:', token.length);

  return fetch(url, {
    method:  opts.method || 'GET',
    headers: {
      'X-Shopify-Access-Token': token,
      'Content-Type':           'application/json',
    },
    body: opts.body,
  });
}

// v7.8 FIXED: Route to correct save function based on active tab
function handlePushToShopify() {
  console.group('[DEBUG handlePushToShopify] STARTING');
  console.log('[DEBUG] centerMode:', centerMode);
  console.log('[DEBUG] centerMode === "bulk"?:', centerMode === 'bulk');
  
  if (centerMode === 'bulk') {
    console.log('[DEBUG] ✓ ROUTING TO saveBulkTiers()');
    console.groupEnd();
    saveBulkTiers();
  } else {
    console.log('[DEBUG] ✓ ROUTING TO pushToShopify()');
    console.groupEnd();
    pushToShopify();
  }
}

async function pushToShopify() {
  var token = localStorage.getItem('ca_shpat') || '';
  if (!token) { showToast('Add your API token in Settings first', 'error'); return; }
  if (!selectedProd) { showToast('Select a product first', 'error'); return; }

  var json = JSON.stringify(buildCfg());
  setStatus('Looking up product…', 'info');

  try {
    var r1  = await shopifyFetch('/admin/api/2024-04/products.json?handle=' + encodeURIComponent(selectedProd.h) + '&fields=id', { method: 'GET' });
    if (!r1.ok) throw new Error('Token error (' + r1.status + ') — check API scopes in Settings');
    var d1  = await r1.json();
    var pid = (d1.products || [])[0] && d1.products[0].id;
    if (!pid) throw new Error('Product not found: ' + selectedProd.h);

    setStatus('Checking metafield…', 'info');
    var r2   = await shopifyFetch('/admin/api/2024-04/products/' + pid + '/metafields.json?namespace=custom&key=addon_groups', { method: 'GET' });
    var d2   = await r2.json();
    var exst = (d2.metafields || []).filter(function(m){ return m.namespace==='custom' && m.key==='addon_groups'; })[0];

    setStatus('Saving…', 'info');
    var r3 = exst
      ? await shopifyFetch('/admin/api/2024-04/products/' + pid + '/metafields/' + exst.id + '.json',
          { method: 'PUT',  body: JSON.stringify({ metafield: { id: exst.id, value: json, type: 'json' } }) })
      : await shopifyFetch('/admin/api/2024-04/products/' + pid + '/metafields.json',
          { method: 'POST', body: JSON.stringify({ metafield: { namespace: 'custom', key: 'addon_groups', value: json, type: 'json' } }) });

    if (!r3.ok) {
      var e = await r3.json().catch(function(){ return {}; });
      throw new Error(JSON.stringify(e.errors || r3.status));
    }
    setStatus('Saved to Shopify!', 'success');
    showToast('Pushed to Shopify ✓', 'success');
    dirty = false;
    localStorage.setItem('ca6_' + selectedProd.h, json);
    renderProdList(filteredProds);
    updateConfiguredLabel();
  } catch(err) {
    console.error('[Push]', err);
    setStatus(err.message, 'error');
    showToast('Push failed', 'error');
  }
}

document.addEventListener('DOMContentLoaded', function() {
  if (localStorage.getItem('ca_shpat')) enablePushBtn();
  document.querySelectorAll('.modal-overlay').forEach(el => {
    el.addEventListener('click', e => { if (e.target === el) el.classList.remove('show'); });
  });
});


function cg(g) {
  return {
    id: g.id || '', label: g.label || '', display: g.display || 'button',
    ...(g.required ? {required: true} : {}),
    ...(g.multiSelect ? {multiSelect: true} : {}),
    ...(g.replaces_main ? {replaces_main: true} : {}),
    options: (g.options || []).map(o => {
      const out = {label: o.label || ''};
      if (o.variant_id) out.variant_id = o.variant_id;
      else out.handle = o.handle || '';
      if (o.image) out.image = o.image;
      if (o.best_seller) out.best_seller = true;
      if (o.recommended) out.recommended = true;
      return out;
    })
  };
}